import { useState, useEffect, useRef, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Upload } from "lucide-react";
import { Shield, LogOut, Settings, MapPin, Users, Mic, Square, Play, Pause, Plus, Trash2, X, Printer, Download, ArrowLeft, User } from "lucide-react";
import ProfileSection from "@/components/app/ProfileSection";
import PatrolScheduler from "@/components/app/PatrolScheduler";
import ThemeToggle from "@/components/ThemeToggle";
import secureflexLogo from "@/assets/secureflex-logo-new.png";
import DashboardBreadcrumb from "@/components/app/DashboardBreadcrumb";
import { useMotion } from "@/lib/safeMotion";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";
import { useBusinessData } from "@/hooks/useBusinessData";
import { supabase } from "@/integrations/supabase/client";
import type { DbOfficer, DbSite, DbShiftPassword, DbCheckpoint } from "@/lib/supabaseQueries";
import { isOfficerLive, lastSeenAgo as lastSeenAgoUtil } from "@/lib/officerStatus";
import { PLANS, getPlan, getPlanPrice, canAddOfficer, canAddSite, getLimitMessage } from "@/lib/planLimits";
import { addLayerToggle } from "@/lib/mapLayers";
import { getAutoPasswordSchedule, setAutoPasswordSchedule, type AutoPasswordSchedule } from "@/lib/sharedStore";

import { Radio, Clock, ClipboardList, Map, AlertTriangle as TriangleAlert, Users as UsersIcon, Building2, Settings as SettingsIcon, CreditCard, ScanFace, CheckCircle2, ShieldCheck, Eye, Key, Copy, ChevronDown, ChevronUp, FileText, AlertTriangle, ShieldAlert, UserCircle, MessageSquare } from "lucide-react";
import SupportTicketForm from "@/components/app/SupportTicketForm";
import RiskManagement from "@/components/app/RiskManagement";
import FaceCaptureModal from "@/components/app/FaceCaptureModal";
import FaceVerificationLogs from "@/components/app/FaceVerificationLogs";
import { useNotifications } from "@/hooks/useNotifications";
import TimesheetSection from "@/components/app/TimesheetSection";
import PromoCodeRedeem from "@/components/app/PromoCodeRedeem";
import MapAddressSearch from "@/components/app/MapAddressSearch";
import IncidentCard from "@/components/app/IncidentCard";

const tabs = [
  { id: "live", label: "Live", icon: Radio },
  { id: "schedule", label: "Schedule", icon: Clock },
  { id: "log", label: "Log", icon: ClipboardList },
  { id: "map", label: "Map", icon: Map },
  { id: "incidents", label: "Incidents", icon: TriangleAlert },
  { id: "risks", label: "Risks", icon: ShieldAlert },
  { id: "timesheets", label: "Timesheets", icon: Clock },
  { id: "facelog", label: "Face Log", icon: ScanFace },
  { id: "officers", label: "Officers", icon: UsersIcon },
  { id: "sites", label: "Sites", icon: Building2 },
  { id: "clients", label: "Clients", icon: UserCircle },
  { id: "profile", label: "Profile", icon: User },
  { id: "setup", label: "Setup", icon: SettingsIcon },
  { id: "billing", label: "Billing", icon: CreditCard },
  { id: "support", label: "Support", icon: MessageSquare },
];

// Mock data removed — all data now comes from the database via useBusinessData hook

// Generate a deterministic QR-like pattern from checkpoint data
const generateQRPattern = (id: string, name: string) => {
  const rects: { x: number; y: number; w: number; h: number }[] = [];
  const seed = (id + name).split('').reduce((a, c) => a + c.charCodeAt(0), 0);
  const size = 5;
  const grid = 100 / size;
  // Position detection patterns (3 corners)
  const addFinder = (ox: number, oy: number) => {
    rects.push({ x: ox, y: oy, w: size * 7, h: size });
    rects.push({ x: ox, y: oy + size * 6, w: size * 7, h: size });
    rects.push({ x: ox, y: oy + size, w: size, h: size * 5 });
    rects.push({ x: ox + size * 6, y: oy + size, w: size, h: size * 5 });
    rects.push({ x: ox + size * 2, y: oy + size * 2, w: size * 3, h: size * 3 });
  };
  addFinder(0, 0);
  addFinder(100 - size * 7, 0);
  addFinder(0, 100 - size * 7);
  // Data modules
  for (let i = 8; i < grid - 1; i++) {
    for (let j = 8; j < grid - 1; j++) {
      if (((seed * (i + 1) * (j + 3) + i * j * 7) % 3) === 0) {
        rects.push({ x: i * size, y: j * size, w: size, h: size });
      }
    }
  }
  for (let i = 0; i < grid; i++) {
    if (((seed + i * 13) % 4) < 2) rects.push({ x: i * size, y: 8 * size, w: size, h: size });
    if (((seed + i * 17) % 4) < 2) rects.push({ x: 8 * size, y: i * size, w: size, h: size });
  }
  return rects;
};

// Mini map component for checkpoint configuration
const ConfigMap = ({ checkpointId, lat, lng, radius, onPositionChange, configMapRef, configMapInstanceRef, configMarkerRef, configCircleRef, userLocation }: {
  checkpointId: string;
  lat: number | null;
  lng: number | null;
  radius: number;
  onPositionChange: (lat: number, lng: number) => void;
  configMapRef: React.RefObject<HTMLDivElement>;
  configMapInstanceRef: React.MutableRefObject<any>;
  configMarkerRef: React.MutableRefObject<any>;
  configCircleRef: React.MutableRefObject<any>;
  userLocation: { lat: number; lng: number } | null;
}) => {
  useEffect(() => {
    if (!configMapRef.current) return;
    const loadMap = async () => {
      const L = await import("leaflet");
      (window as any).L = L;
      if (!document.getElementById("leaflet-css")) {
        const link = document.createElement("link");
        link.id = "leaflet-css";
        link.rel = "stylesheet";
        link.href = "https://unpkg.com/leaflet@1.9.4/dist/leaflet.css";
        document.head.appendChild(link);
      }
      await new Promise(r => setTimeout(r, 200));
      if (!configMapRef.current) return;
      configMapRef.current.innerHTML = "";

      const center = lat != null && lng != null ? { lat, lng } : userLocation || { lat: 0, lng: 0 };
      const map = L.map(configMapRef.current).setView([center.lat, center.lng], 17);
      configMapInstanceRef.current = map;

      L.tileLayer("https://{s}.basemaps.cartocdn.com/dark_all/{z}/{x}/{y}{r}.png", {
        attribution: '&copy; CARTO',
      }).addTo(map);

      // Draggable marker
      const goldIcon = L.divIcon({
        html: '<div style="width:20px;height:20px;border-radius:50%;background:#F0B429;border:3px solid white;box-shadow:0 0 12px rgba(240,180,41,0.5);cursor:grab;"></div>',
        iconSize: [20, 20],
        iconAnchor: [10, 10],
        className: '',
      });

      const marker = L.marker([center.lat, center.lng], { icon: goldIcon, draggable: true }).addTo(map);
      configMarkerRef.current = marker;

      // Geofence circle
      const circle = L.circle([center.lat, center.lng], {
        radius: radius,
        color: '#F0B429',
        fillColor: 'rgba(240,180,41,0.15)',
        fillOpacity: 0.3,
        weight: 2,
        dashArray: '6 4',
      }).addTo(map);
      configCircleRef.current = circle;

      marker.on('dragend', () => {
        const pos = marker.getLatLng();
        circle.setLatLng(pos);
        onPositionChange(pos.lat, pos.lng);
      });
    };
    loadMap();
    return () => {
      if (configMapInstanceRef.current) {
        configMapInstanceRef.current.remove();
        configMapInstanceRef.current = null;
      }
    };
  }, [checkpointId]);

  // Update circle radius when it changes
  useEffect(() => {
    if (configCircleRef.current) {
      configCircleRef.current.setRadius(radius);
    }
  }, [radius]);

  return (
    <div
      ref={configMapRef}
      className="w-full h-[280px] rounded-xl border border-[hsl(var(--app-gold)/0.3)] overflow-hidden bg-[hsl(var(--app-surface-2))]"
    />
  );
};

const BusinessOwnerDashboard = () => {
  const { motion, AnimatePresence } = useMotion();
  const navigate = useNavigate();
  const { signOut, user, profile } = useAuth();
  const {
    officers: officersList,
    sites,
    lateReports,
    shiftPasswords: shiftPasswordsList,
    checkpoints: dbCheckpoints,
    checkIns,
    incidents: dbIncidents,
    schedules,
    isLoading,
    addOfficer: addOfficerMutation,
    updateOfficer: updateOfficerMutation,
    removeOfficer: removeOfficerMutation,
    addSite: addSiteMutation,
    updateSite: updateSiteMutation,
    removeSite: removeSiteMutation,
    addCheckpoint: addCheckpointMutation,
    removeCheckpoint: removeCheckpointMutation,
    issuePassword: issuePasswordMutation,
    issueAllPasswords: issueAllPasswordsMutation,
    addSchedule: addScheduleMutation,
    updateSchedule: updateScheduleMutation,
    removeSchedule: removeScheduleMutation,
    addIncident: addIncidentMutation,
    updateIncident: updateIncidentMutation,
    generateRotation: generateRotationMutation,
  } = useBusinessData();

  // Build a map of shift passwords keyed by officer_id for quick lookup
  const shiftPasswords: Record<string, DbShiftPassword> = {};
  shiftPasswordsList.forEach(p => { shiftPasswords[p.officer_id] = p; });

  const [activeTab, setActiveTab] = useState("live");
  const [activeSite, setActiveSite] = useState("all");
  const [selectedPlan, setSelectedPlan] = useState<string | null>(null);
  const [subscription, setSubscription] = useState<{ plan: string; status: string; trial_end: string; activated_at: string | null } | null>(null);
  const [showUpgradeConfirm, setShowUpgradeConfirm] = useState(false);
  const [expandedBillingPlan, setExpandedBillingPlan] = useState<string | null>(null);
  const [clientsList, setClientsList] = useState<Array<{ id: string; client_name: string; client_email: string | null; client_phone: string | null; site_id: string | null; site_owner_user_id: string | null; notes: string | null; added_via: string; created_at: string }>>([]);
  const [clientsLoading, setClientsLoading] = useState(false);
  const [clientSearch, setClientSearch] = useState("");
  const [showAddClient, setShowAddClient] = useState(false);
  const [newClient, setNewClient] = useState({ name: "", email: "", phone: "", siteId: "", notes: "" });
  const [addingClient, setAddingClient] = useState(false);
  const [dismissedOverdue, setDismissedOverdue] = useState<Set<string>>(new Set());

  // Browser notifications for security operator 
  const { requestPermission, notify, beep } = useNotifications();
  const prevIncidentCountRef = useRef(dbIncidents.length);

  useEffect(() => { requestPermission(); }, [requestPermission]);

  // Clean up stale officer statuses on mount
  useEffect(() => {
    supabase.functions.invoke("cleanup-stale-officers").catch(() => {});
  }, []);

  // Notify on new incidents
  useEffect(() => {
    if (dbIncidents.length > prevIncidentCountRef.current && prevIncidentCountRef.current > 0) {
      const newest = dbIncidents[0];
      beep();
      notify({
        title: "New Incident Reported",
        body: `${newest?.title || "Incident"} — ${newest?.severity || "unknown"} severity`,
        tag: `incident-${newest?.id}`,
        requireInteraction: true,
        url: "/app/business-owner",
      });
    }
    prevIncidentCountRef.current = dbIncidents.length;
  }, [dbIncidents.length, notify, beep]);

  // Notify on overdue schedules
  const overdueNotifiedRef = useRef<Set<string>>(new Set());
  useEffect(() => {
    const now = new Date();
    schedules.forEach((s: any) => {
      if (s.status === "completed" || overdueNotifiedRef.current.has(s.id)) return;
      const due = new Date(s.due_at);
      if (due < now && now.getTime() - due.getTime() < 5 * 60 * 1000) {
        overdueNotifiedRef.current.add(s.id);
        const officer = officersList.find((o: any) => o.id === s.officer_id);
        notify({
          title: "â° Overdue Check-in",
          body: `${officer?.name || "Officer"} missed check-in due at ${due.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}`,
          tag: `overdue-${s.id}`,
          url: "/app/business-owner",
        });
      }
    });
  }, [schedules, officersList, notify]);

  // Fetch clients (business_clients table) for security operator
  useEffect(() => {
    if (activeTab !== "clients" || !user?.id) return;
    const fetchClients = async () => {
      setClientsLoading(true);
      const { data } = await supabase
        .from("business_clients")
        .select("*")
        .eq("business_owner_id", user.id)
        .order("created_at", { ascending: false });
      setClientsList(data || []);
      setClientsLoading(false);
    };
    fetchClients();
  }, [activeTab, user?.id]);

  const handleAddClient = async () => {
    if (!newClient.name.trim()) { toast.error("Client name is required"); return; }
    if (!user?.id) return;
    setAddingClient(true);
    const { error } = await supabase.from("business_clients").insert({
      business_owner_id: user.id,
      client_name: newClient.name.trim(),
      client_email: newClient.email.trim() || null,
      client_phone: newClient.phone.trim() || null,
      site_id: newClient.siteId || null,
      notes: newClient.notes.trim() || null,
      added_via: "manual",
    });
    setAddingClient(false);
    if (error) { toast.error("Failed to add client"); return; }
    toast.success("Client added successfully");
    setNewClient({ name: "", email: "", phone: "", siteId: "", notes: "" });
    setShowAddClient(false);
    // Refresh
    const { data } = await supabase.from("business_clients").select("*").eq("business_owner_id", user.id).order("created_at", { ascending: false });
    setClientsList(data || []);
  };

  const handleRemoveClient = async (clientId: string) => {
    if (!user?.id) return;
    await supabase.from("business_clients").delete().eq("id", clientId);
    setClientsList(prev => prev.filter(c => c.id !== clientId));
    toast.success("Client removed");
  };

  // Fetch subscription data
  useEffect(() => {
    const fetchSub = async () => {
      if (!user?.id) return;
      const { data } = await supabase.from("subscriptions").select("*").eq("user_id", user.id).maybeSingle();
      if (data) setSubscription(data as any);
    };
    fetchSub();
  }, [user?.id]);
  // Only show onboarding on first login (no sites yet = new user)
  const [showOnboarding, setShowOnboarding] = useState(false);
  const [onboardingChecked, setOnboardingChecked] = useState(false);
  const [showTutorial, setShowTutorial] = useState(false);
  const [tutorialStep, setTutorialStep] = useState(0);
  const [dismissedWarning, setDismissedWarning] = useState(false);

  // Auto-show onboarding for first-time users
  useEffect(() => {
    if (onboardingChecked) return;
    if (isLoading || !user?.id) return;
    
    const isFirstTimeUser = sites.length === 0 && officersList.length === 0;
    const hasDismissed = localStorage.getItem(`secureflex_onboarding_dismissed_${user.id}`);
    
    if (isFirstTimeUser && !hasDismissed) {
      setShowOnboarding(true);
    }
    setOnboardingChecked(true);
  }, [isLoading, sites.length, officersList.length, onboardingChecked, user?.id]);

  const handleDismissOnboarding = () => {
    setShowOnboarding(false);
    if (user?.id) {
      localStorage.setItem(`secureflex_onboarding_dismissed_${user.id}`, "true");
    }
  };
  const [incidentFilter, setIncidentFilter] = useState("all");
  const [playingVoice, setPlayingVoice] = useState<string | null>(null);
  const [showAddSite, setShowAddSite] = useState(false);
  const [showAddOfficer, setShowAddOfficer] = useState<string | null>(null);
  const [newSite, setNewSite] = useState({ name: "", address: "", lat: null as number | null, lng: null as number | null });
  const [newOfficer, setNewOfficer] = useState({ name: "", username: "" });
  const [showAddCheckpointModal, setShowAddCheckpointModal] = useState(false);
  const [newCheckpoint, setNewCheckpoint] = useState({ name: "", zone: "", siteId: "", method: "gps", radius: 25, lat: null as number | null, lng: null as number | null });
  const siteMapRef = useRef<HTMLDivElement>(null);
  const siteMapInstanceRef = useRef<any>(null);
  const siteMarkerRef = useRef<any>(null);
  const addCpMapRef = useRef<HTMLDivElement>(null);
  const addCpMapInstanceRef = useRef<any>(null);
  const addCpMarkerRef = useRef<any>(null);
  const addCpCircleRef = useRef<any>(null);
  const [configCheckpoint, setConfigCheckpoint] = useState<string | null>(null);
  const [checkpointConfigs, setCheckpointConfigs] = useState<Record<string, { method: string; radius: number; lat: number | null; lng: number | null }>>({});
  const configMapRef = useRef<HTMLDivElement>(null);
  const configMapInstanceRef = useRef<any>(null);
  const configMarkerRef = useRef<any>(null);
  const configCircleRef = useRef<any>(null);
  const mapRef = useRef<HTMLDivElement>(null);
  const mapInstanceRef = useRef<any>(null);
  const userMarkerRef = useRef<any>(null);
  const userAccuracyCircleRef = useRef<any>(null);
  const userLocationRef = useRef<{ lat: number; lng: number } | null>(null);
  const mapLoadedForTab = useRef<string | null>(null);
  const officerMarkersRef = useRef<Record<string, any>>({});
  const checkpointMarkersRef = useRef<Record<string, any>>({});
  const trailPolylineRef = useRef<any>(null);
  const [userLocation, setUserLocation] = useState<{ lat: number; lng: number } | null>(null);

  // Real-time clock
  const [currentTime, setCurrentTime] = useState(new Date());
  useEffect(() => {
    const timer = setInterval(() => setCurrentTime(new Date()), 1000);
    return () => clearInterval(timer);
  }, []);

  // Add Officer modal state
  const [showAddOfficerModal, setShowAddOfficerModal] = useState(false);
  const [newOfficerForm, setNewOfficerForm] = useState({ name: "", phone: "", siteId: "", email: "", username: "" });
  const [editOfficer, setEditOfficer] = useState<DbOfficer | null>(null);
  const [editForm, setEditForm] = useState({ name: "", phone: "", email: "", siteId: "", username: "" });
  const [resetPasswordResult, setResetPasswordResult] = useState<{ officerName: string; tempPassword: string } | null>(null);
  const [resetPasswordLoading, setResetPasswordLoading] = useState(false);
  const [expandedLateLog, setExpandedLateLog] = useState<string | null>(null);
  const [faceCaptureOfficer, setFaceCaptureOfficer] = useState<{ id: string; name: string; photoUrl: string | null } | null>(null);

  // Add Schedule modal state
  const [showAddScheduleModal, setShowAddScheduleModal] = useState(false);
  const [newSchedule, setNewSchedule] = useState({ officerId: "", checkpointId: "", siteId: "", dueDate: "", dueTime: "" });

  // Add Incident modal state
  const [showAddIncidentModal, setShowAddIncidentModal] = useState(false);
  const [newIncident, setNewIncident] = useState({ title: "", description: "", severity: "low", category: "Other", siteId: "", officerId: "" });

  // CSV Import state
  const [showCsvImportModal, setShowCsvImportModal] = useState(false);
  const [csvParsedOfficers, setCsvParsedOfficers] = useState<Array<{ name: string; phone: string; email: string; siteName: string; valid: boolean; error?: string }>>([]);
  const [csvImporting, setCsvImporting] = useState(false);
  const csvFileRef = useRef<HTMLInputElement>(null);

  const handleCsvFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (evt) => {
      const text = evt.target?.result as string;
      const lines = text.split(/\r?\n/).filter(l => l.trim());
      if (lines.length < 2) { toast.error("CSV must have a header row and at least one data row"); return; }
      const header = lines[0].split(",").map(h => h.trim().toLowerCase());
      const nameIdx = header.findIndex(h => h.includes("name"));
      const phoneIdx = header.findIndex(h => h.includes("phone"));
      const emailIdx = header.findIndex(h => h.includes("email"));
      const siteIdx = header.findIndex(h => h.includes("site"));
      if (nameIdx === -1) { toast.error("CSV must have a 'name' column"); return; }
      const parsed = lines.slice(1).map(line => {
        const cols = line.split(",").map(c => c.trim());
        const name = cols[nameIdx] || "";
        const phone = phoneIdx >= 0 ? cols[phoneIdx] || "" : "";
        const email = emailIdx >= 0 ? cols[emailIdx] || "" : "";
        const siteName = siteIdx >= 0 ? cols[siteIdx] || "" : "";
        const errors: string[] = [];
        if (!name) errors.push("Missing name");
        if (siteName && !sites.find(s => s.name.toLowerCase() === siteName.toLowerCase())) errors.push("Unknown site");
        return { name, phone, email, siteName, valid: errors.length === 0, error: errors.join(", ") };
      });
      setCsvParsedOfficers(parsed);
      setShowCsvImportModal(true);
    };
    reader.readAsText(file);
    e.target.value = "";
  };

  const handleCsvImport = async () => {
    const validRows = csvParsedOfficers.filter(r => r.valid);
    if (validRows.length === 0) { toast.error("No valid rows to import"); return; }
    const planObj = getPlan(subscription?.plan);
    const remaining = planObj.maxOfficers === Infinity ? Infinity : planObj.maxOfficers - officersList.length;
    if (remaining < validRows.length) {
      toast.error(`Your ${planObj.name} plan only allows ${remaining} more officer${remaining !== 1 ? "s" : ""}. Upgrade to import more.`);
      return;
    }
    setCsvImporting(true);
    let count = 0;
    for (const row of validRows) {
      const site = sites.find(s => s.name.toLowerCase() === row.siteName.toLowerCase());
      try {
        const officer = await addOfficerMutation.mutateAsync({
          name: row.name,
          phone: row.phone || null,
          email: row.email || null,
          site_id: site?.id || null,
          status: "offline",
          lat: -29.884 + (Math.random() - 0.5) * 0.01,
          lng: 30.947 + (Math.random() - 0.5) * 0.01,
        });
        // Auto-create login account if email provided
        if (row.email && officer?.id) {
          try {
            await supabase.functions.invoke("create-officer-account", {
              body: { email: row.email, officerId: officer.id, name: row.name },
            });
          } catch {}
        }
        count++;
      } catch {}
    }
    setCsvImporting(false);
    setShowCsvImportModal(false);
    setCsvParsedOfficers([]);
    toast.success(`${count} officer${count > 1 ? "s" : ""} imported successfully`);
  };

  const handleOpenEdit = (officer: DbOfficer) => {
    setEditOfficer(officer);
    setEditForm({ name: officer.name, phone: officer.phone || "", email: officer.email || "", siteId: officer.site_id || "", username: officer.username || "" });
  };

  const handleSaveEdit = async () => {
    if (!editOfficer) return;
    if (!editForm.name.trim()) { toast.error("Name is required"); return; }
    if (!editForm.siteId) { toast.error("Please assign a site"); return; }
    try {
      await updateOfficerMutation.mutateAsync({
        id: editOfficer.id,
        updates: {
          name: editForm.name,
          phone: editForm.phone || null,
          email: editForm.email || null,
          site_id: editForm.siteId,
          username: editForm.username.trim().toLowerCase() || null,
        },
      });
      setEditOfficer(null);
      toast.success(`${editForm.name} updated successfully`);
    } catch (err: any) {
      toast.error(err.message || "Failed to update officer");
    }
  };

  const handleResetOfficerPassword = async (officerId: string) => {
    setResetPasswordLoading(true);
    setResetPasswordResult(null);
    try {
      const { data, error } = await supabase.functions.invoke("reset-officer-password", {
        body: { officerId },
      });
      if (error || data?.error) {
        toast.error(data?.error || "Failed to reset password");
      } else {
        setResetPasswordResult({ officerName: data.officerName, tempPassword: data.tempPassword });
        toast.success(`Password reset for ${data.officerName}`);
      }
    } catch {
      toast.error("Network error. Please try again.");
    }
    setResetPasswordLoading(false);
  };

  // Quick temp login for officers having login issues
  const [tempLoginResult, setTempLoginResult] = useState<{ officerName: string; username: string; tempPassword: string } | null>(null);
  const [tempLoginLoading, setTempLoginLoading] = useState(false);

  const handleQuickTempLogin = async (officerId: string, officerName: string) => {
    setTempLoginLoading(true);
    try {
      const { data, error } = await supabase.functions.invoke("reset-officer-password", {
        body: { officerId },
      });
      if (error || data?.error) {
        toast.error(data?.error || "Failed to generate temp login");
      } else {
        const officer = officersList.find(o => o.id === officerId);
        setTempLoginResult({
          officerName: data.officerName || officerName,
          username: officer?.username || "",
          tempPassword: data.tempPassword,
        });
        toast.success(`Temp login ready for ${officerName}`);
      }
    } catch {
      toast.error("Network error. Please try again.");
    }
    setTempLoginLoading(false);
  };

  const [manualLoginLoading, setManualLoginLoading] = useState<string | null>(null);
  const handleManualLogin = async (officerId: string, officerName: string) => {
    setManualLoginLoading(officerId);
    try {
      const officer = officersList.find(o => o.id === officerId);
      const site = sites.find(s => s.id === officer?.site_id);
      
      await updateOfficerMutation.mutateAsync({
        id: officerId,
        updates: {
          status: "live",
          last_seen: new Date().toISOString(),
          lat: site?.lat || null,
          lng: site?.lng || null
        }
      });
      toast.success(`Manual login successful for ${officerName}`);
      setEditOfficer(null);
    } catch (err: any) {
      toast.error(err.message || "Failed to manually log in officer");
    } finally {
      setManualLoginLoading(null);
    }
  };

  const [syncAccountLoading, setSyncAccountLoading] = useState<string | null>(null);

  const handleFixOfficerAccount = async (officerId: string, username: string, name: string) => {
    setSyncAccountLoading(officerId);
    try {
      const { data, error } = await supabase.functions.invoke("create-officer-account", {
        body: { username: username.toLowerCase(), officerId, name },
      });
      
      if (error || data?.error) {
        console.error("Fix account error:", error, data);
        let msg = data?.error || "Failed to fix account. Please try again.";
        if (error) {
          msg = error.message;
          try {
            const errorBody = await (error as any).context?.json();
            if (errorBody?.error) msg = errorBody.error;
            else if (errorBody?.message) msg = errorBody.message;
          } catch (e) {}
        }
        toast.error(msg);
      } else {
        toast.success(`Account successfully linked for ${name}. They can now sign in.`);
      }
    } catch (err: any) {
      console.error("Fix account networking error:", err);
      toast.error("Network error: " + (err.message || "Please try again."));
    } finally {
      setSyncAccountLoading(null);
    }
  };

  const [autoSchedule, setAutoScheduleState] = useState<AutoPasswordSchedule>(getAutoPasswordSchedule);
  const [showPasswordModal, setShowPasswordModal] = useState<string | null>(null);
  const [issuedPassword, setIssuedPassword] = useState<DbShiftPassword | null>(null);
  const [showAutoScheduleModal, setShowAutoScheduleModal] = useState(false);
  const [scheduleForm, setScheduleForm] = useState<AutoPasswordSchedule>(getAutoPasswordSchedule);

  // Auto-schedule password generation check
  useEffect(() => {
    if (!autoSchedule.enabled) return;
    const checkAutoGenerate = () => {
      const now = new Date();
      const [h, m] = autoSchedule.time.split(":").map(Number);
      if (now.getHours() === h && now.getMinutes() === m && now.getSeconds() < 2) {
        issueAllPasswordsMutation.mutate({ officerIds: officersList.map(o => o.id), expiryHours: autoSchedule.expiryHours });
        toast.success("Shift passwords auto-generated for all officers");
      }
    };
    const interval = setInterval(checkAutoGenerate, 1000);
    return () => clearInterval(interval);
  }, [autoSchedule, officersList]);

  const handleIssuePassword = async (officerId: string) => {
    try {
      const pwd = await issuePasswordMutation.mutateAsync({ officerId, expiryHours: autoSchedule.expiryHours || 12 });
      setIssuedPassword(pwd);
      setShowPasswordModal(officerId);
      toast.success("Shift password issued");
    } catch (err: any) {
      toast.error(err.message || "Failed to issue password");
    }
  };

  const handleIssueAllPasswords = () => {
    issueAllPasswordsMutation.mutate({ officerIds: officersList.map(o => o.id), expiryHours: autoSchedule.expiryHours || 12 });
    toast.success("Shift passwords issued for all officers");
  };

  const handleSaveAutoSchedule = () => {
    setAutoPasswordSchedule(scheduleForm);
    setAutoScheduleState(scheduleForm);
    setShowAutoScheduleModal(false);
    toast.success(scheduleForm.enabled ? `Auto-generation set for ${scheduleForm.time} daily` : "Auto-generation disabled");
  };

  const handleAddNewOfficer = async () => {
    if (!canAddOfficer(subscription?.plan, officersList.length)) {
      toast.error(getLimitMessage(subscription?.plan, "officer"));
      setActiveTab("billing");
      return;
    }
    if (!newOfficerForm.name.trim()) { toast.error("Officer name is required"); return; }
    if (!newOfficerForm.username.trim()) { toast.error("Username is required"); return; }
    if (!newOfficerForm.siteId) { toast.error("Please assign a site"); return; }
    try {
      const officer = await addOfficerMutation.mutateAsync({
        name: newOfficerForm.name,
        phone: newOfficerForm.phone || null,
        email: newOfficerForm.email || null,
        site_id: newOfficerForm.siteId,
        status: "offline",
        lat: -29.884 + (Math.random() - 0.5) * 0.01,
        lng: 30.947 + (Math.random() - 0.5) * 0.01,
      });
      // Auto-create login account with username
      if (officer?.id) {
        try {
          const { data: accountResult, error: accountError } = await supabase.functions.invoke("create-officer-account", {
            body: { username: newOfficerForm.username, email: newOfficerForm.email || null, officerId: officer.id, name: newOfficerForm.name },
          });
          if (accountError) {
            console.error("Account creation failed:", accountError);
            toast.warning(`Officer added but login account creation failed.`);
          } else if (accountResult?.tempPassword && !accountResult?.alreadyExisted) {
            toast.success(`Account created! Username: ${newOfficerForm.username} · Temp password: ${accountResult.tempPassword}`, { duration: 15000 });
          } else if (accountResult?.alreadyExisted) {
            toast.info("Existing account linked to this officer");
          }
        } catch (accountErr) {
          console.error("Account creation error:", accountErr);
        }
      }
      setNewOfficerForm({ name: "", phone: "", siteId: "", email: "", username: "" });
      setShowAddOfficerModal(false);
      toast.success(`${newOfficerForm.name} added successfully`);
    } catch (err: any) {
      toast.error(err.message || "Failed to add officer");
    }
  };

  const handleRemoveOfficer = async (officerId: string) => {
    try {
      await removeOfficerMutation.mutateAsync(officerId);
      toast.success("Officer removed");
    } catch (err: any) {
      toast.error(err.message || "Failed to remove officer");
    }
  };

  // Compute setup progress from real data
  const setupSteps = {
    site: sites.length > 0,
    officers: officersList.length > 0,
    checkpoints: dbCheckpoints.length > 0,
    passwords: shiftPasswordsList.length > 0,
  };
  const completedSteps = Object.values(setupSteps).filter(Boolean).length;

  // Helper: get site name from id (must be defined before first usage below)
  const getSiteName = (siteId: string | null) => {
    if (!siteId) return "";
    return sites.find(s => s.id === siteId)?.name || "";
  };

  // Compute live checkpoint data from DB
  const liveCheckpoints = dbCheckpoints.map(cp => {
    const cpCheckIns = checkIns.filter(ci => ci.checkpoint_id === cp.id);
    const latest = cpCheckIns[0];
    const officer = latest ? officersList.find(o => o.id === latest.officer_id) : null;
    return {
      id: cp.id,
      name: cp.name,
      zone: cp.zone || getSiteName(cp.site_id),
      siteId: cp.site_id,
      method: cp.method,
      radius: cp.radius || 25,
      hasLocation: cp.lat != null && cp.lng != null,
      lastChecked: latest ? new Date(latest.checked_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) : null,
      lastOfficer: officer?.name || null,
      lastMethod: latest?.method || null,
      status: latest ? "done" : "pending" as string,
    };
  });

  // Using shared isOfficerLive from @/lib/officerStatus (imported at top)

  const scheduleNow = new Date();
  const computedSchedule = schedules.map(s => {
    const checkpoint = dbCheckpoints.find(cp => cp.id === s.checkpoint_id);
    const officer = officersList.find(o => o.id === s.officer_id);
    const site = sites.find(st => st.id === s.site_id);
    const checkIn = checkIns.find(ci => ci.checkpoint_id === s.checkpoint_id && ci.officer_id === s.officer_id);
    const dueDate = new Date(s.due_at);
    const dueTime = dueDate.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    const dueDateStr = dueDate.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });
    const isOverdue = !checkIn && s.status !== "completed" && dueDate < scheduleNow;
    const overdueMinutes = isOverdue ? Math.round((scheduleNow.getTime() - dueDate.getTime()) / 60000) : 0;
    return {
      id: s.id,
      due: dueTime,
      dueDate: dueDateStr,
      dueFullDate: dueDate.toISOString(),
      checkpoint: checkpoint?.name || "—",
      site: site?.name || "—",
      site_id: s.site_id,
      officer: officer?.name || "—",
      officer_id: s.officer_id,
      status: checkIn || s.status === "completed" ? "done" : isOverdue ? "overdue" : s.status === "pending" ? "pending" : "due",
      actual: checkIn ? new Date(checkIn.checked_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) : null,
      actualDate: checkIn ? new Date(checkIn.checked_at).toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' }) : null,
      variance: checkIn?.variance_minutes != null && checkIn.variance_minutes !== 0
        ? (checkIn.variance_minutes >= 0 ? `+${checkIn.variance_minutes}m` : `${checkIn.variance_minutes}m`)
        : null,
      method: checkIn?.method || null,
      isOverdue,
      overdueMinutes,
    };
  });

  const computedLog = checkIns.map(ci => {
    const officer = officersList.find(o => o.id === ci.officer_id);
    const checkpoint = dbCheckpoints.find(cp => cp.id === ci.checkpoint_id);
    const site = sites.find(s => s.id === ci.site_id);
    const coords = ci.coords as any;
    const checkedDate = new Date(ci.checked_at);
    return {
      id: ci.id,
      time: checkedDate.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
      date: checkedDate.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' }),
      fullTimestamp: checkedDate.toLocaleString(),
      checkpoint: checkpoint?.name || "—",
      site: site?.name || "—",
      site_id: ci.site_id,
      officer: officer?.name || "—",
      scheduled: ci.scheduled_at ? new Date(ci.scheduled_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) : "—",
      variance: ci.variance_minutes != null && ci.variance_minutes !== 0
        ? (ci.variance_minutes >= 0 ? `+${ci.variance_minutes}m` : `${ci.variance_minutes}m`)
        : null,
      method: ci.method || "—",
      coords: coords ? `${(coords.lat ?? 0).toFixed(4)}, ${(coords.lng ?? 0).toFixed(4)}` : "—",
      onTime: ci.on_time !== false,
    };
  });

  const computedIncidents = dbIncidents.map(inc => {
    const officer = officersList.find(o => o.id === inc.officer_id);
    const site = sites.find(s => s.id === inc.site_id);
    const created = new Date(inc.created_at);
    return {
      id: inc.id,
      title: inc.title,
      description: inc.description || "",
      severity: inc.severity,
      category: inc.category || "Other",
      time: created.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
      date: created.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' }),
      fullTimestamp: created.toLocaleString(),
      officer: officer?.name || "—",
      site: site?.name || "—",
      site_id: inc.site_id,
      escalated: inc.escalated || false,
      resolved: inc.resolved || false,
      hasVoiceNote: !!inc.audio_url,
      voiceDuration: 0,
      audio_url: inc.audio_url,
      photo_url: inc.photo_url,
      ai_analysis: inc.ai_analysis,
      resolution_notes: (inc as any).resolution_notes || null,
    };
  });

  const filteredCheckpoints = activeSite === "all" ? liveCheckpoints : liveCheckpoints.filter(cp => cp.siteId === activeSite);
  const filteredSchedule = activeSite === "all" ? computedSchedule : computedSchedule.filter(s => s.site_id === activeSite);
  const filteredLog = activeSite === "all" ? computedLog : computedLog.filter(l => l.site_id === activeSite);
  const filteredIncidents = computedIncidents.filter(inc => {
    const siteMatch = activeSite === "all" || inc.site_id === activeSite;
    const filterMatch = incidentFilter === "all" || inc.severity === incidentFilter || (incidentFilter === "Escalated" && inc.escalated);
    return siteMatch && filterMatch;
  });
  const filteredOfficers = activeSite === "all" ? officersList : officersList.filter(o => o.site_id === activeSite);

  // (getSiteName and getOfficersForSite moved above first usage)
  // Helper: get officers assigned to a site
  const getOfficersForSite = (siteId: string) => officersList.filter(o => o.site_id === siteId);

  const mapInitializedRef = useRef(false);

  useEffect(() => {
    // Smart fallback: use first known officer/site coordinate from DB
    const getSmartFallback = () => {
      const o = officersList.find(o => o.lat != null && o.lng != null);
      if (o) return { lat: o.lat!, lng: o.lng! };
      const s = sites.find(s => s.lat != null && s.lng != null);
      if (s) return { lat: s.lat!, lng: s.lng! };
      return { lat: 0, lng: 0 };
    };

    if (!navigator.geolocation) {
      const fallback = getSmartFallback();
      setUserLocation(fallback);
      userLocationRef.current = fallback;
      return;
    }
    const watchId = navigator.geolocation.watchPosition(
      (pos) => {
        const loc = { lat: pos.coords.latitude, lng: pos.coords.longitude };
        userLocationRef.current = loc;
        setUserLocation(loc);
        if (userMarkerRef.current) {
          userMarkerRef.current.setLatLng([loc.lat, loc.lng]);
          userMarkerRef.current.bindPopup(`<div style="color:#000"><b>&#9679; YOU (Live)</b><br/><span style="font-family:monospace;font-size:11px">${loc.lat.toFixed(6)}, ${loc.lng.toFixed(6)}</span><br/><i>Updated ${new Date().toLocaleTimeString()}</i></div>`);
        }
        if (userAccuracyCircleRef.current) {
          userAccuracyCircleRef.current.setLatLng([loc.lat, loc.lng]);
        }
      },
      () => {
        const fallback = getSmartFallback();
        userLocationRef.current = fallback;
        setUserLocation(fallback);
      },
      { enableHighAccuracy: true, maximumAge: 5000, timeout: 15000 }
    );
    return () => navigator.geolocation.clearWatch(watchId);
  }, [officersList, sites]);

  // Load Leaflet map — uses a callback ref to ensure DOM is ready
  
  const initMap = useCallback(async (container: HTMLDivElement) => {
    if (!container) return;
    // Prevent double-init for same tab
    const key = `${activeTab}-${activeSite}`;
    if (mapLoadedForTab.current === key && mapInstanceRef.current) return;
    mapLoadedForTab.current = key;

    const L = await import("leaflet");
    if (!document.getElementById("leaflet-css")) {
      const link = document.createElement("link");
      link.id = "leaflet-css";
      link.rel = "stylesheet";
      link.href = "https://unpkg.com/leaflet@1.9.4/dist/leaflet.css";
      document.head.appendChild(link);
      await new Promise(r => setTimeout(r, 300));
    }

    // Clean up any previous map
    if (mapInstanceRef.current) {
      mapInstanceRef.current.remove();
      mapInstanceRef.current = null;
    }
    container.innerHTML = "";
    officerMarkersRef.current = {};
    checkpointMarkersRef.current = {};
    trailPolylineRef.current = null;

    const loc = userLocationRef.current || { lat: 0, lng: 0 };
    const map = L.map(container, { zoomControl: true }).setView([loc.lat, loc.lng], 16);
    mapInstanceRef.current = map;

    L.tileLayer("https://{s}.basemaps.cartocdn.com/dark_all/{z}/{x}/{y}{r}.png", {
      attribution: '&copy; <a href="https://carto.com/">CARTO</a>',
      maxZoom: 19,
    }).addTo(map);
    addLayerToggle(L, map);

    // Your location marker
    const youIcon = L.divIcon({
      html: '<div style="width:24px;height:24px;border-radius:50%;background:#3B82F6;border:3px solid white;box-shadow:0 0 12px rgba(59,130,246,0.6);animation:pulse 2s infinite;"></div>',
      iconSize: [24, 24],
      iconAnchor: [12, 12],
      className: '',
    });
    const youMarker = L.marker([loc.lat, loc.lng], { icon: youIcon }).addTo(map)
      .bindPopup(`<div style="color:#000"><b>&#9679; YOU</b><br/><span style="font-family:monospace;font-size:11px">${loc.lat.toFixed(6)}, ${loc.lng.toFixed(6)}</span></div>`);
    userMarkerRef.current = youMarker;

    const accCircle = L.circle([loc.lat, loc.lng], {
      radius: 30, color: '#3B82F6', fillColor: 'rgba(59,130,246,0.1)', fillOpacity: 0.3, weight: 1, dashArray: '4 4',
    }).addTo(map);
    userAccuracyCircleRef.current = accCircle;

    // Officers — use their REAL GPS coordinates from the database
    const filtOfficers = activeSite === "all" ? officersList : officersList.filter(o => o.site_id === activeSite);

    const bounds: [number, number][] = [[loc.lat, loc.lng]];

    filtOfficers.forEach((officer) => {
      const oLat = officer.lat;
      const oLng = officer.lng;
      if (oLat == null || oLng == null) return; // skip officers with no GPS data
      bounds.push([oLat, oLng]);
      const isLive = isOfficerLive(officer);
      const icon = L.divIcon({
        html: isLive
          ? `<div style="position:relative;width:20px;height:20px;border-radius:50%;background:#10B981;border:2px solid white;box-shadow:0 0 10px rgba(16,185,129,0.5);"><div style="position:absolute;inset:-4px;border-radius:50%;border:2px solid rgba(16,185,129,0.3);animation:pulse 2s infinite;"></div></div>`
          : '<div style="width:16px;height:16px;border-radius:50%;background:#6B7280;border:2px solid white;opacity:0.7;"></div>',
        iconSize: isLive ? [20, 20] : [16, 16], iconAnchor: isLive ? [10, 10] : [8, 8], className: '',
      });
      const marker = L.marker([oLat, oLng], { icon }).addTo(map)
        .bindPopup(`<div style="color:#000"><b>${officer.name}</b>${isLive ? '' : ' (Offline)'}<br/>${getSiteName(officer.site_id)}<br/>Check-ins: ${officer.checkins}<br/><span style="font-family:monospace;font-size:10px">${oLat.toFixed(6)}, ${oLng.toFixed(6)}</span></div>`)
        .bindTooltip(officer.name, { permanent: true, direction: "top", offset: [0, -12], className: "officer-name-label" });
      
      officerMarkersRef.current[officer.id] = marker;
    });

    // Checkpoints from DB
    const cps = activeSite === "all" ? dbCheckpoints : dbCheckpoints.filter(cp => cp.site_id === activeSite);
    cps.forEach((cp) => {
      if (cp.lat == null || cp.lng == null) return; // skip checkpoints without coordinates
      const pos: [number, number] = [cp.lat, cp.lng];
      bounds.push(pos);
      const latestCheckIn = checkIns.find(ci => ci.checkpoint_id === cp.id);
      const done = !!latestCheckIn;
      const icon = L.divIcon({
        html: `<div style="width:14px;height:14px;border-radius:50%;background:${done ? '#F0B429' : '#EF4444'};border:2px solid white;box-shadow:0 0 6px ${done ? 'rgba(240,180,41,0.4)' : 'rgba(239,68,68,0.4)'};"></div>`,
        iconSize: [14, 14], iconAnchor: [7, 7], className: '',
      });
      const marker = L.marker(pos, { icon }).addTo(map)
        .bindPopup(`<div style="color:#000"><b>${cp.name}</b><br/>${cp.zone || ''}${latestCheckIn ? `<br/>Checked ${new Date(latestCheckIn.checked_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}` : '<br/><i>Not checked today</i>'}</div>`);
      
      checkpointMarkersRef.current[cp.id] = { marker, circle: null };
      
      if (cp.method === "gps" && (cp.radius ?? 0) > 0) {
        const circle = L.circle(pos, { radius: cp.radius!, color: done ? '#F0B429' : '#EF4444', fillColor: done ? 'rgba(240,180,41,0.08)' : 'rgba(239,68,68,0.08)', fillOpacity: 0.3, weight: 1, dashArray: '4 4' }).addTo(map);
        checkpointMarkersRef.current[cp.id].circle = circle;
      }
    });

    // Patrol trail from visited checkpoints
    const visitedPositions = cps
      .filter(cp => checkIns.some(ci => ci.checkpoint_id === cp.id) && cp.lat != null && cp.lng != null)
      .map(cp => [cp.lat!, cp.lng!] as [number, number]);
    if (visitedPositions.length > 1) {
      trailPolylineRef.current = L.polyline(visitedPositions, { color: '#F0B429', dashArray: '8 6', weight: 2, opacity: 0.6 }).addTo(map);
    }

    // Fit map to show all markers
    if (bounds.length > 1) {
      try { map.fitBounds(bounds, { padding: [40, 40], maxZoom: 17 }); } catch {}
    }

    setTimeout(() => map.invalidateSize(), 200);
  }, [activeSite, activeTab]);

  // Clean up map when leaving map tab
  useEffect(() => {
    if (activeTab !== "map") {
      if (mapInstanceRef.current) {
        mapInstanceRef.current.remove();
        mapInstanceRef.current = null;
        userMarkerRef.current = null;
        userAccuracyCircleRef.current = null;
        mapLoadedForTab.current = null;
        officerMarkersRef.current = {};
        checkpointMarkersRef.current = {};
        trailPolylineRef.current = null;
      }
    }
  }, [activeTab]);

  // Sync officer markers reactively
  useEffect(() => {
    const map = mapInstanceRef.current;
    if (!map || activeTab !== "map") return;

    import("leaflet").then(L => {
      officersList.forEach(officer => {
        const marker = officerMarkersRef.current[officer.id];
        if (!marker) {
          // If marker doesn't exist yet but has coordinates, we'd need to add it.
          // For simplicity, we mostly cater to UPDATING existing markers here.
          // initMap already handles the initial set based on activeSite.
          return;
        }

        const oLat = officer.lat;
        const oLng = officer.lng;
        if (oLat != null && oLng != null) {
          const isLive = isOfficerLive(officer);
          const currentPos = marker.getLatLng();
          
          // Only update if position or status changed
          if (currentPos.lat !== oLat || currentPos.lng !== oLng) {
            marker.setLatLng([oLat, oLng]);
            marker.getPopup()?.setContent(`<div style="color:#000"><b>${officer.name}</b>${isLive ? '' : ' (Offline)'}<br/>${getSiteName(officer.site_id)}<br/>Check-ins: ${officer.checkins}<br/><span style="font-family:monospace;font-size:10px">${oLat.toFixed(6)}, ${oLng.toFixed(6)}</span></div>`);
          }
          
          // Update icon if status changed
          const icon = L.divIcon({
            html: isLive
              ? `<div style="position:relative;width:20px;height:20px;border-radius:50%;background:#10B981;border:2px solid white;box-shadow:0 0 10px rgba(16,185,129,0.5);"><div style="position:absolute;inset:-4px;border-radius:50%;border:2px solid rgba(16,185,129,0.3);animation:pulse 2s infinite;"></div></div>`
              : '<div style="width:16px;height:16px;border-radius:50%;background:#6B7280;border:2px solid white;opacity:0.7;"></div>',
            iconSize: isLive ? [20, 20] : [16, 16], iconAnchor: isLive ? [10, 10] : [8, 8], className: '',
          });
          marker.setIcon(icon);
        }
      });
    });
  }, [officersList, activeTab]);

  // Sync checkpoint markers reactively (when check-ins update)
  useEffect(() => {
    const map = mapInstanceRef.current;
    if (!map || activeTab !== "map") return;

    Object.entries(checkpointMarkersRef.current).forEach(([id, item]) => {
      const cp = dbCheckpoints.find(c => c.id === id);
      if (!cp) return;
      
      const latestCheckIn = checkIns.find(ci => ci.checkpoint_id === cp.id);
      const done = !!latestCheckIn;
      
      import("leaflet").then(L => {
        const icon = L.divIcon({
          html: `<div style="width:14px;height:14px;border-radius:50%;background:${done ? '#F0B429' : '#EF4444'};border:2px solid white;box-shadow:0 0 6px ${done ? 'rgba(240,180,41,0.4)' : 'rgba(239,68,68,0.4)'};"></div>`,
          iconSize: [14, 14], iconAnchor: [7, 7], className: '',
        });
        item.marker.setIcon(icon);
        item.marker.getPopup()?.setContent(`<div style="color:#000"><b>${cp.name}</b><br/>${cp.zone || ''}${latestCheckIn ? `<br/>Checked ${new Date(latestCheckIn.checked_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}` : '<br/><i>Not checked today</i>'}</div>`);
        
        if (item.circle) {
          item.circle.setStyle({ color: done ? '#F0B429' : '#EF4444', fillColor: done ? 'rgba(240,180,41,0.08)' : 'rgba(239,68,68,0.08)' });
        }
      });
    });

    // Update trail
    const cps = activeSite === "all" ? dbCheckpoints : dbCheckpoints.filter(cp => cp.site_id === activeSite);
    const visitedPositions = cps
      .filter(cp => checkIns.some(ci => ci.checkpoint_id === cp.id) && cp.lat != null && cp.lng != null)
      .map(cp => [cp.lat!, cp.lng!] as [number, number]);
    
    if (trailPolylineRef.current) {
      trailPolylineRef.current.setLatLngs(visitedPositions);
    } else if (visitedPositions.length > 1) {
      import("leaflet").then(L => {
        trailPolylineRef.current = L.polyline(visitedPositions, { color: '#F0B429', dashArray: '8 6', weight: 2, opacity: 0.6 }).addTo(map);
      });
    }
  }, [checkIns, activeTab, dbCheckpoints]);

  // Callback ref for the map div
  const mapCallbackRef = useCallback((node: HTMLDivElement | null) => {
    if (node && activeTab === "map") {
      (mapRef as any).current = node;
      // Small delay to ensure the AnimatePresence animation completes
      setTimeout(() => initMap(node), 100);
    }
  }, [activeTab, initMap]);

  const flyToOfficer = (officer: DbOfficer) => {
    if (mapInstanceRef.current) {
      mapInstanceRef.current.flyTo([officer.lat, officer.lng], 18, { duration: 1.5 });
    }
  };

  const handlePlayVoice = (id: string) => {
    if (playingVoice === id) {
      setPlayingVoice(null);
    } else {
      setPlayingVoice(id);
      // Simulate playback ending
      setTimeout(() => setPlayingVoice(null), 3000);
    }
  };

  const handleAddSite = async () => {
    if (!canAddSite(subscription?.plan, sites.length)) {
      toast.error(getLimitMessage(subscription?.plan, "site"));
      setActiveTab("billing");
      return;
    }
    if (!newSite.name.trim()) { toast.error("Site name is required"); return; }
    try {
      await addSiteMutation.mutateAsync({ 
        name: newSite.name, 
        address: newSite.address,
        lat: newSite.lat,
        lng: newSite.lng,
        owner_id: user?.id,
      } as any);
      setNewSite({ name: "", address: "", lat: null, lng: null });
      setShowAddSite(false);
      if (siteMapInstanceRef.current) { siteMapInstanceRef.current.remove(); siteMapInstanceRef.current = null; }
      toast.success("Site added successfully");
    } catch (err: any) {
      toast.error(err.message || "Failed to add site");
    }
  };

  const handleAddCheckpoint = async () => {
    if (!newCheckpoint.name.trim()) { toast.error("Checkpoint name is required"); return; }
    if (!newCheckpoint.siteId) { toast.error("Please select a site"); return; }
    try {
      await addCheckpointMutation.mutateAsync({
        name: newCheckpoint.name,
        zone: newCheckpoint.zone || null,
        site_id: newCheckpoint.siteId,
        method: newCheckpoint.method,
        radius: newCheckpoint.method === "gps" ? newCheckpoint.radius : null,
        lat: newCheckpoint.lat,
        lng: newCheckpoint.lng,
      });
      setNewCheckpoint({ name: "", zone: "", siteId: "", method: "gps", radius: 25, lat: null, lng: null });
      setShowAddCheckpointModal(false);
      if (addCpMapInstanceRef.current) { addCpMapInstanceRef.current.remove(); addCpMapInstanceRef.current = null; }
      toast.success("Checkpoint added successfully");
    } catch (err: any) {
      toast.error(err.message || "Failed to add checkpoint");
    }
  };

  const handleAddOfficerToSite = async (siteId: string) => {
    if (!canAddOfficer(subscription?.plan, officersList.length)) {
      toast.error(getLimitMessage(subscription?.plan, "officer"));
      setActiveTab("billing");
      return;
    }
    if (!newOfficer.name.trim()) { toast.error("Officer name is required"); return; }
    const username = newOfficer.username?.trim() || newOfficer.name.toLowerCase().replace(/\s/g, '.') + Math.floor(Math.random() * 100);
    
    try {
      const officer = await addOfficerMutation.mutateAsync({
        name: newOfficer.name.trim(),
        username: username.toLowerCase(),
        site_id: siteId,
        status: "offline",
        lat: -29.884 + (Math.random() - 0.5) * 0.01,
        lng: 30.947 + (Math.random() - 0.5) * 0.01,
      });

      // Auto-create login account for the new officer
      if (officer?.id) {
        try {
          const { data: accountResult, error: accountError } = await supabase.functions.invoke("create-officer-account", {
            body: { 
              username: username.toLowerCase(), 
              officerId: officer.id, 
              name: newOfficer.name 
            },
          });
          
          if (accountError) {
            console.error("Account creation failed:", accountError);
            toast.warning(`Officer added but login account creation failed.`);
          } else if (accountResult?.tempPassword) {
            toast.success(`Account created! Username: ${username} · Temp password: ${accountResult.tempPassword}`, { duration: 15000 });
          }
        } catch (accountErr) {
          console.error("Account creation error:", accountErr);
        }
      }

      setNewOfficer({ name: "", username: "" });
      setShowAddOfficer(null);
      toast.success("Officer added to site");
    } catch (err: any) {
      toast.error(err.message || "Failed to add officer");
    }
  };

  const handleRemoveSite = async (siteId: string) => {
    try {
      await removeSiteMutation.mutateAsync(siteId);
      toast.success("Site removed");
    } catch (err: any) {
      toast.error(err.message || "Failed to remove site");
    }
  };

  const handleAddSchedule = async () => {
    if (!newSchedule.officerId) { toast.error("Please select an officer"); return; }
    if (!newSchedule.siteId) { toast.error("Please select a site"); return; }
    if (!newSchedule.dueDate || !newSchedule.dueTime) { toast.error("Please set due date and time"); return; }
    const dueAt = new Date(`${newSchedule.dueDate}T${newSchedule.dueTime}`);
    if (isNaN(dueAt.getTime())) { toast.error("Invalid date/time"); return; }
    try {
      await addScheduleMutation.mutateAsync({
        officer_id: newSchedule.officerId,
        checkpoint_id: newSchedule.checkpointId || null,
        site_id: newSchedule.siteId,
        due_at: dueAt.toISOString(),
        status: "pending",
      });
      setNewSchedule({ officerId: "", checkpointId: "", siteId: "", dueDate: "", dueTime: "" });
      setShowAddScheduleModal(false);
      toast.success("Schedule added successfully");
    } catch (err: any) {
      toast.error(err.message || "Failed to add schedule");
    }
  };

  const handleAddIncident = async () => {
    if (!newIncident.title.trim()) { toast.error("Title is required"); return; }
    if (!newIncident.siteId) { toast.error("Please select a site"); return; }
    try {
      await addIncidentMutation.mutateAsync({
        title: newIncident.title.trim(),
        description: newIncident.description.trim() || null,
        severity: newIncident.severity,
        category: newIncident.category || null,
        site_id: newIncident.siteId,
        officer_id: newIncident.officerId || null,
      });
      setNewIncident({ title: "", description: "", severity: "low", category: "Other", siteId: "", officerId: "" });
      setShowAddIncidentModal(false);
      toast.success("Incident reported successfully");
    } catch (err: any) {
      toast.error(err.message || "Failed to report incident");
    }
  };

  const [daysLeft, setDaysLeft] = useState(30);
  useEffect(() => {
    const calc = () => {
      const trialEnd = subscription?.trial_end ? new Date(subscription.trial_end) : null;
      return trialEnd ? Math.max(0, Math.ceil((trialEnd.getTime() - Date.now()) / 86400000)) : 30;
    };
    setDaysLeft(calc());
    const interval = setInterval(() => setDaysLeft(calc()), 60000);
    return () => clearInterval(interval);
  }, [subscription?.trial_end]);
  const isTrialExpired = subscription?.status === 'trial' && daysLeft <= 0;
  const isInWarningPeriod = subscription?.status === 'trial' && daysLeft > 0 && daysLeft <= 7;
  const isActive = subscription?.status === 'active';

  // ---- Lemon Squeezy checkout helper ----
  const CHECKOUT_URLS: Record<string, string> = {
    core: import.meta.env.VITE_LS_CORE_URL || "",
    professional: import.meta.env.VITE_LS_PROFESSIONAL_URL || "",
    essential_pro: import.meta.env.VITE_LS_ESSENTIAL_PRO_URL || "",
  };

  const openLemonCheckout = (planKey: string) => {
    const baseUrl = CHECKOUT_URLS[planKey];
    if (!baseUrl || baseUrl.includes("REPLACE_WITH")) {
      toast.info("Payment setup in progress — contact us at secureflexgroup@outlook.com to upgrade now.");
      return;
    }
    const params = new URLSearchParams();
    if (user?.email) params.set("checkout[email]", user.email);
    if (user?.id) params.set("checkout[custom][user_id]", user.id);
    params.set("checkout[custom][plan]", planKey);
    const url = `${baseUrl}?${params.toString()}`;
    window.open(url, "_blank", "noopener,noreferrer");
    toast.success("Opening secure checkout — complete your payment there to activate.", { duration: 5000 });
  };

  // Show warning toast once per day during last 7 days
  useEffect(() => {
    if (isInWarningPeriod && !isActive) {
      const today = new Date().toDateString();
      const lastShown = localStorage.getItem('sf_trial_warning_date');
      if (lastShown !== today) {
        localStorage.setItem('sf_trial_warning_date', today);
        toast.warning(
          `âš ï¸ Your free trial ends in ${daysLeft} day${daysLeft === 1 ? '' : 's'}! Upgrade now to keep your access.`,
          { duration: 10000 }
        );
      }
    }
  }, [isInWarningPeriod, isActive, daysLeft]);

  // If trial expired, show blocking screen
  if (isTrialExpired && !isActive) {
    return (
      <div className="min-h-screen app-bg font-outfit flex items-center justify-center">
        <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} className="app-card rounded-2xl p-8 max-w-lg mx-4 text-center">
          <div className="w-16 h-16 rounded-full bg-[hsl(var(--app-red)/0.15)] border-2 border-[hsl(var(--app-red))] flex items-center justify-center mx-auto mb-6">
            <Clock className="w-8 h-8 text-[hsl(var(--app-red))]" />
          </div>
          <h2 className="font-bebas text-3xl text-[hsl(var(--app-text-primary))]">YOUR FREE TRIAL HAS ENDED</h2>
          <p className="text-sm text-[hsl(var(--app-text-secondary))] mt-2 mb-6">
            Your 30-day free trial has expired. To continue using SecureFlex and access all your data, please select a plan below.
          </p>
          <div className="grid gap-3 mb-6">
            {[
              { key: "core", name: "CORE", price: "$16/mo", desc: "Up to 5 officers, 2 sites" },
              { key: "professional", name: "PROFESSIONAL", price: "$28/mo", desc: "Up to 15 officers, 5 sites" },
              { key: "essential_pro", name: "ESSENTIAL PRO", price: "$52/mo", desc: "Unlimited officers & sites" },
            ].map((plan) => (
              <button
                key={plan.name}
                onClick={() => openLemonCheckout(plan.key)}
                className="flex items-center justify-between p-4 rounded-xl border border-[hsl(var(--app-border-subtle))] hover:border-[hsl(var(--app-gold))] hover:bg-[hsl(var(--app-gold)/0.05)] transition-all text-left group"
              >
                <div>
                  <p className="font-bebas text-lg text-[hsl(var(--app-text-primary))]">{plan.name}</p>
                  <p className="text-xs text-[hsl(var(--app-text-muted))]">{plan.desc}</p>
                </div>
                <div className="flex items-center gap-2">
                  <span className="font-bebas text-xl text-[hsl(var(--app-gold))]">{plan.price}</span>
                  <span className="text-xs text-[hsl(var(--app-gold))] opacity-0 group-hover:opacity-100 transition-opacity">Pay Now →</span>
                </div>
              </button>
            ))}
          </div>
          <div className="flex gap-3 justify-center">
            <button onClick={() => navigate("/")} className="text-sm text-[hsl(var(--app-text-muted))] hover:text-[hsl(var(--app-text-secondary))] transition-colors">
              Back to website
            </button>
            <button onClick={signOut} className="text-sm text-[hsl(var(--app-text-muted))] hover:text-[hsl(var(--app-text-secondary))] transition-colors">
              Sign out
            </button>
          </div>
        </motion.div>
      </div>
    );
  }

  return (
    <div className="min-h-screen app-bg font-outfit pb-16 md:pb-0">
      {/* Trial warning banner - last 7 days */}
      {isInWarningPeriod && !dismissedWarning && (
        <div className="sticky top-0 z-50 bg-[hsl(var(--app-red)/0.9)] backdrop-blur-sm px-4 py-2.5 flex items-center justify-center gap-3">
          <Clock className="w-4 h-4 text-white flex-shrink-0" />
          <p className="text-sm text-white font-medium">
            Your free trial ends in <span className="font-bold">{daysLeft} day{daysLeft === 1 ? '' : 's'}</span> — 
            <button onClick={() => setActiveTab("billing")} className="underline ml-1 font-bold hover:opacity-80">Upgrade now</button>
          </p>
          <button onClick={() => setDismissedWarning(true)} className="text-white/70 hover:text-white ml-2">
            <X className="w-4 h-4" />
          </button>
        </div>
      )}
      {/* First-time user onboarding — auto-trigger */}

      {showOnboarding && !isLoading && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-[hsl(var(--app-bg)/0.95)] backdrop-blur-sm">
          <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} className="app-card rounded-2xl p-8 max-w-md mx-4 text-center relative">
            <button onClick={() => navigate("/")} className="absolute top-4 left-4 flex items-center gap-1.5 text-xs text-[hsl(var(--app-text-muted))] hover:text-[hsl(var(--app-text-secondary))] transition-colors">
              <ArrowLeft className="w-3.5 h-3.5" /> Back
            </button>
            <div className="w-14 h-14 rounded-2xl bg-[hsl(var(--app-gold)/0.1)] border border-[hsl(var(--app-gold)/0.2)] flex items-center justify-center mx-auto mb-4">
              <Shield className="w-7 h-7 text-[hsl(var(--app-gold))]" />
            </div>
            <h2 className="font-bebas text-3xl text-[hsl(var(--app-text-primary))]">Welcome to SecureFlex</h2>
            <p className="text-sm text-[hsl(var(--app-text-secondary))] mt-2 mb-1">PROACTIVE DIGITAL FORTRESS</p>
            <p className="text-xs text-[hsl(var(--app-text-muted))] mb-6">Let's get your security operations set up in under 5 minutes.</p>
            <div className="flex flex-col gap-3">
              <button onClick={() => { handleDismissOnboarding(); setShowTutorial(true); setTutorialStep(0); }} className="w-full py-3 rounded-xl bg-[hsl(var(--app-gold))] text-[hsl(var(--app-bg))] font-semibold hover:brightness-110 transition-all flex items-center justify-center gap-2">
                <FileText className="w-4 h-4" /> Quick Setup Guide
              </button>
              <button onClick={handleDismissOnboarding} className="w-full py-2.5 rounded-xl border border-[hsl(var(--app-border-subtle))] text-[hsl(var(--app-text-muted))] text-sm hover:bg-[hsl(var(--app-surface-2))] transition-all">
                Skip — I'll explore on my own
              </button>
            </div>
          </motion.div>
        </div>
      )}

      {/* Step-by-step tutorial guide */}
      {showTutorial && (() => {
        const tutorialSteps: { title: string; description: string; action: string; tab: string; iconEl: React.ReactNode; done: boolean }[] = [
          {
            title: "Add Your First Site",
            description: "A site is the location you're securing — an office building, estate, shopping centre, or warehouse. Add its name and address so officers know where they're deployed.",
            action: "Go to Sites",
            tab: "sites",
            iconEl: <Building2 className="w-7 h-7 text-[hsl(var(--app-gold))]" />,
            done: setupSteps.site,
          },
          {
            title: "Register Your Officers",
            description: "Add your security officers by name and contact details. Each officer receives their own login credentials to check in, report incidents, and view their schedules.",
            action: "Go to Officers",
            tab: "officers",
            iconEl: <UsersIcon className="w-7 h-7 text-[hsl(var(--app-gold))]" />,
            done: setupSteps.officers,
          },
          {
            title: "Configure Checkpoints",
            description: "Checkpoints are specific patrol points at your site — Main Gate, Parking Lot, Lobby, Perimeter Fence. Set GPS coordinates so the system can verify officer locations.",
            action: "Go to Setup",
            tab: "setup",
            iconEl: <MapPin className="w-7 h-7 text-[hsl(var(--app-gold))]" />,
            done: setupSteps.checkpoints,
          },
          {
            title: "Issue Shift Passwords",
            description: "Shift passwords are unique one-time codes that verify an officer is authorised for duty. Issue passwords before each shift — they expire automatically for security.",
            action: "Go to Officers",
            tab: "officers",
            iconEl: <Key className="w-7 h-7 text-[hsl(var(--app-gold))]" />,
            done: setupSteps.passwords,
          },
          {
            title: "You're Ready to Go",
            description: "Your command centre is set up. Monitor officers live, review check-in logs, manage incidents, and generate PDF reports — all from this dashboard. You can reopen this guide anytime from the setup bar.",
            action: "Open Dashboard",
            tab: "live",
            iconEl: <CheckCircle2 className="w-7 h-7 text-[hsl(var(--app-green))]" />,
            done: completedSteps === 4,
          },
        ];
        const step = tutorialSteps[tutorialStep] || tutorialSteps[0];
        const isLast = tutorialStep === tutorialSteps.length - 1;
        const stepNum = tutorialStep + 1;
        return (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-[hsl(var(--app-bg)/0.95)] backdrop-blur-sm">
            <motion.div key={tutorialStep} initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} className="app-card rounded-2xl p-8 max-w-md mx-4 relative">
              <button onClick={() => setShowTutorial(false)} className="absolute top-4 right-4 text-[hsl(var(--app-text-muted))] hover:text-[hsl(var(--app-text-secondary))] transition-colors">
                <X className="w-4 h-4" />
              </button>

              {/* Progress bar */}
              <div className="flex items-center gap-2 mb-6">
                <span className="text-[10px] font-semibold text-[hsl(var(--app-text-muted))] uppercase tracking-wider">Step {stepNum} of {tutorialSteps.length}</span>
                <div className="flex-1 h-1 rounded-full bg-[hsl(var(--app-border-subtle))] overflow-hidden">
                  <div className="h-full rounded-full bg-[hsl(var(--app-gold))] transition-all duration-300" style={{ width: `${(stepNum / tutorialSteps.length) * 100}%` }} />
                </div>
              </div>

              <div className="text-center">
                <div className="w-14 h-14 rounded-2xl bg-[hsl(var(--app-gold)/0.08)] border border-[hsl(var(--app-gold)/0.15)] flex items-center justify-center mx-auto mb-4">
                  {step.iconEl}
                </div>
                <h3 className="font-bebas text-2xl text-[hsl(var(--app-text-primary))] mb-2">{step.title}</h3>
                {step.done && !isLast && (
                  <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-[10px] font-bold bg-[hsl(var(--app-green)/0.15)] text-[hsl(var(--app-green))] mb-2">
                    <CheckCircle2 className="w-3 h-3" /> COMPLETED
                  </span>
                )}
                <p className="text-sm text-[hsl(var(--app-text-secondary))] leading-relaxed">{step.description}</p>
              </div>

              <div className="flex gap-2 mt-6">
                {tutorialStep > 0 && (
                  <button onClick={() => setTutorialStep(tutorialStep - 1)} className="flex-1 py-2.5 rounded-xl border border-[hsl(var(--app-border-subtle))] text-[hsl(var(--app-text-secondary))] text-sm font-medium hover:bg-[hsl(var(--app-surface-2))] transition-all flex items-center justify-center gap-1">
                    <ArrowLeft className="w-3.5 h-3.5" /> Back
                  </button>
                )}
                {!isLast ? (
                  <button onClick={() => setTutorialStep(tutorialStep + 1)} className="flex-1 py-2.5 rounded-xl bg-[hsl(var(--app-gold))] text-[hsl(var(--app-bg))] text-sm font-semibold hover:brightness-110 transition-all flex items-center justify-center gap-1">
                    Next <ChevronDown className="w-3.5 h-3.5 -rotate-90" />
                  </button>
                ) : (
                  <button onClick={() => { setShowTutorial(false); setActiveTab(step.tab); }} className="flex-1 py-2.5 rounded-xl bg-[hsl(var(--app-gold))] text-[hsl(var(--app-bg))] text-sm font-semibold hover:brightness-110 transition-all flex items-center justify-center gap-1">
                    <Shield className="w-3.5 h-3.5" /> Launch Dashboard
                  </button>
                )}
              </div>
              {!isLast && (
                <button onClick={() => { setShowTutorial(false); setActiveTab(step.tab); }} className="w-full mt-3 text-xs text-[hsl(var(--app-gold))] hover:underline flex items-center justify-center gap-1">
                  {step.action} now <ChevronDown className="w-3 h-3 -rotate-90" />
                </button>
              )}
            </motion.div>
          </div>
        );
      })()}

      {/* Top bar */}
      <header className="sticky top-0 z-40 border-b border-[hsl(var(--app-border-subtle))] app-surface-1 px-4 py-3">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2 min-w-0">
            <button onClick={() => navigate("/")} className="p-1.5 rounded-lg text-[hsl(var(--app-text-muted))] hover:text-[hsl(var(--app-text-secondary))] hover:bg-[hsl(var(--app-surface-2))] transition-colors shrink-0" title="Back to website">
              <ArrowLeft className="w-4 h-4" />
            </button>
            <img src={secureflexLogo} alt="SecureFlex Logo" className="h-12 w-12 object-contain shrink-0" />
            <span className="font-bebas text-base sm:text-lg tracking-wider text-[hsl(var(--app-text-primary))]">SECUREFLEX</span>
            <span className="text-[10px] px-2 py-0.5 rounded-full bg-[hsl(var(--app-gold)/0.15)] text-[hsl(var(--app-gold))] whitespace-nowrap shrink-0 hidden sm:inline uppercase">{profile?.full_name || "My Business"}</span>
          </div>
          <div className="flex items-center gap-2 shrink-0">
            <div className="hidden sm:flex flex-col items-end">
              <span className="font-mono-data text-xs text-[hsl(var(--app-gold))]">{currentTime.toLocaleTimeString()}</span>
              <span className="text-[9px] text-[hsl(var(--app-text-muted))]">{currentTime.toLocaleDateString(undefined, { weekday: 'short', day: 'numeric', month: 'short' })}</span>
            </div>
            <ThemeToggle />
            <button onClick={async () => { await signOut(); navigate("/app"); }} className="text-[hsl(var(--app-text-muted))] hover:text-[hsl(var(--app-text-secondary))] flex items-center gap-1 text-xs">
              <LogOut className="w-4 h-4" />
            </button>
          </div>
        </div>
        {/* Site filter - separate row */}
        {sites.length > 0 && (
          <div className="flex items-center gap-2 mt-2 overflow-x-auto pb-1 -mb-1">
            <button onClick={() => setActiveSite("all")} className={`px-3 py-1 rounded-full text-xs font-medium whitespace-nowrap transition-all shrink-0 ${
              activeSite === "all" ? "bg-[hsl(var(--app-gold))] text-[hsl(var(--app-bg))]" : "border border-[hsl(var(--app-border-subtle))] text-[hsl(var(--app-text-muted))]"
            }`}>All Sites</button>
            {sites.map((s) => (
              <button key={s.id} onClick={() => setActiveSite(s.id)} className={`px-3 py-1 rounded-full text-xs font-medium whitespace-nowrap transition-all shrink-0 ${
                activeSite === s.id ? "bg-[hsl(var(--app-gold))] text-[hsl(var(--app-bg))]" : "border border-[hsl(var(--app-border-subtle))] text-[hsl(var(--app-text-muted))]"
              }`}>{s.name}</button>
            ))}
          </div>
        )}
      </header>

      <DashboardBreadcrumb role="Security Operator" activeTab={tabs.find(t => t.id === activeTab)?.label || activeTab} />

      {/* Setup progress */}
      {completedSteps < 4 && (
        <div className="border-b border-[hsl(var(--app-border-subtle))] px-4 py-2 app-surface-1">
          <div className="max-w-5xl mx-auto flex items-center gap-3">
            <p className="text-xs text-[hsl(var(--app-text-muted))]">Setup:</p>
            <div className="flex items-center gap-1.5">
              {[
                { label: "Site", done: setupSteps.site },
                { label: "Officers", done: setupSteps.officers },
                { label: "Checkpoints", done: setupSteps.checkpoints },
                { label: "Passwords", done: setupSteps.passwords },
              ].map(s => (
                <span key={s.label} className={`text-[10px] px-2 py-0.5 rounded-full font-medium ${s.done ? "bg-[hsl(var(--app-green)/0.15)] text-[hsl(var(--app-green))]" : "bg-[hsl(var(--app-border-subtle))] text-[hsl(var(--app-text-muted))]"}`}>
                  {s.done ? "✓" : "○ "} {s.label}
                </span>
              ))}
            </div>
            <button onClick={() => { setShowTutorial(true); setTutorialStep(0); }} className="ml-auto text-[10px] text-[hsl(var(--app-gold))] hover:underline">
              Guide
            </button>
          </div>
        </div>
      )}

      {/* Tabs - desktop */}
      <div className="hidden md:block border-b border-[hsl(var(--app-border-subtle))] app-surface-1">
        <div className="max-w-6xl mx-auto px-4">
          <div className="flex flex-wrap gap-x-0.5 gap-y-0">
            {tabs.map((tab) => {
              const Icon = tab.icon;
              return (
                <button key={tab.id} onClick={() => setActiveTab(tab.id)} className={`px-3 py-2.5 text-[11px] font-semibold uppercase tracking-wider border-b-2 transition-colors whitespace-nowrap flex items-center gap-1.5 ${
                  activeTab === tab.id ? "border-[hsl(var(--app-gold))] text-[hsl(var(--app-gold))]" : "border-transparent text-[hsl(var(--app-text-muted))] hover:text-[hsl(var(--app-text-secondary))]"
                }`}><Icon className="w-3.5 h-3.5" /> {tab.label}</button>
              );
            })}
          </div>
        </div>
      </div>


      <main className="max-w-6xl mx-auto p-4">
        <AnimatePresence mode="wait">
          <motion.div key={activeTab} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }}>

            {/* ========== LIVE TAB ========== */}
            {activeTab === "live" && (
              <div className="space-y-4">
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                  {[
                    { value: `${filteredCheckpoints.length > 0 ? Math.round((filteredCheckpoints.filter(c => c.status === "done").length / filteredCheckpoints.length) * 100) : 0}%`, label: "COVERAGE", color: "app-gold" },
                    { value: String(filteredLog.length), label: "CHECK-INS TODAY", color: "app-blue" },
                    { value: String(filteredOfficers.filter(o => isOfficerLive(o)).length), label: "ACTIVE OFFICERS", color: "app-green" },
                    { value: String(filteredIncidents.filter(i => !i.resolved).length), label: "OPEN INCIDENTS", color: "app-red" },
                  ].map((s) => (
                    <div key={s.label} className="app-card rounded-xl p-4 text-center">
                      <p className={`font-bebas text-3xl text-[hsl(var(--${s.color}))]`}>{s.value}</p>
                      <p className="text-[10px] font-semibold tracking-wider text-[hsl(var(--app-text-muted))] uppercase mt-1">{s.label}</p>
                    </div>
                  ))}
                </div>

                {/* Overdue Check-in Alerts */}
                {filteredSchedule.filter(s => s.isOverdue && !dismissedOverdue.has(s.id)).length > 0 && (
                  <div className="space-y-2">
                    <h3 className="font-bebas text-lg text-[hsl(var(--app-red))] flex items-center gap-2">
                      <TriangleAlert className="w-4 h-4" /> OVERDUE CHECK-INS
                      <button
                        onClick={() => {
                          const ids = filteredSchedule.filter(s => s.isOverdue).map(s => s.id);
                          setDismissedOverdue(prev => { const next = new Set(prev); ids.forEach(id => next.add(id)); return next; });
                          toast.success("All overdue alerts dismissed from live view");
                        }}
                        className="ml-auto text-[10px] font-outfit font-medium text-[hsl(var(--app-text-muted))] hover:text-[hsl(var(--app-red))] transition-colors"
                      >
                        Dismiss All
                      </button>
                    </h3>
                    {filteredSchedule.filter(s => s.isOverdue && !dismissedOverdue.has(s.id)).map(s => (
                      <div key={s.id} className="app-card rounded-xl p-3 border-l-4 border-l-[hsl(var(--app-red))]">
                        <div className="flex items-center justify-between">
                          <div>
                            <p className="text-sm font-semibold text-[hsl(var(--app-text-primary))]">{s.officer} — {s.checkpoint}</p>
                            <p className="text-xs text-[hsl(var(--app-text-muted))]">{s.site} · Due at {s.due}</p>
                          </div>
                          <span className="px-2 py-1 rounded-full text-[10px] font-bold bg-[hsl(var(--app-red)/0.15)] text-[hsl(var(--app-red))]">
                            +{s.overdueMinutes}m LATE
                          </span>
                        </div>
                        <div className="flex gap-2 mt-2">
                          <button
                            onClick={async () => {
                              // Send notification to officer via Supabase realtime or browser notification
                              const officer = officersList.find(o => o.id === s.officer_id);
                              // Create an in-app late report as a nudge record
                              await supabase.from("late_reports").insert({
                                officer_id: s.officer_id || "",
                                site: s.site,
                                checkpoint: s.checkpoint,
                                late_minutes: s.overdueMinutes,
                                reason: "Nudged by security operator",
                                details: `Security operator sent a check-in reminder for ${s.checkpoint} (due at ${s.due})`,
                              });
                              beep();
                              notify({
                                title: "Check-in Reminder Sent",
                                body: `Nudge sent to ${officer?.name || "Officer"} for ${s.checkpoint}`,
                                tag: `nudge-${s.id}`,
                              });
                              toast.success(`Reminder sent to ${officer?.name || "Officer"}`);
                            }}
                            className="flex-1 py-1.5 px-3 rounded-lg text-xs font-semibold bg-[hsl(var(--app-gold)/0.15)] text-[hsl(var(--app-gold))] hover:bg-[hsl(var(--app-gold)/0.25)] transition-colors flex items-center justify-center gap-1.5"
                          >
                            <Radio className="w-3 h-3" /> Nudge Officer
                          </button>
                          <button
                            onClick={() => {
                              setDismissedOverdue(prev => new Set(prev).add(s.id));
                              toast("Dismissed from live view", { description: "Still visible in Log & Schedule tabs" });
                            }}
                            className="py-1.5 px-3 rounded-lg text-xs font-medium border border-[hsl(var(--app-border-subtle))] text-[hsl(var(--app-text-muted))] hover:bg-[hsl(var(--app-surface-2))] transition-colors flex items-center gap-1.5"
                          >
                            <X className="w-3 h-3" /> Dismiss
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}

                {/* Officer Status Panel */}
                <div className="space-y-2">
                  <h3 className="font-bebas text-lg text-[hsl(var(--app-text-primary))] flex items-center gap-2">
                    <Users className="w-4 h-4 text-[hsl(var(--app-gold))]" /> OFFICER STATUS
                    <span className="text-xs font-outfit font-normal text-[hsl(var(--app-text-muted))] ml-auto">
                      {filteredOfficers.filter(o => isOfficerLive(o)).length} / {filteredOfficers.length} active
                    </span>
                  </h3>
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
                    {filteredOfficers.map(officer => {
                      const lastSeen = officer.last_seen ? new Date(officer.last_seen) : null;
                      const staleMins = lastSeen ? (Date.now() - lastSeen.getTime()) / 60000 : Infinity;
                      const isLive = officer.status === "live" && staleMins < 5;
                      const lastSeenAgo = lastSeen ? (() => {
                        const mins = Math.floor(staleMins);
                        if (mins < 1) return "Just now";
                        if (mins < 60) return `${mins}m ago`;
                        const hrs = Math.floor(mins / 60);
                        if (hrs < 24) return `${hrs}h ago`;
                        return `${Math.floor(hrs / 24)}d ago`;
                      })() : null;
                      const siteName = getSiteName(officer.site_id);

                      return (
                        <div key={officer.id} className={`app-card rounded-xl p-3 border-l-4 ${isLive ? "border-l-[hsl(var(--app-green))]" : "border-l-[hsl(var(--app-border-subtle))]"}`}>
                          <div className="flex items-center gap-3">
                            <div className="relative">
                              <div className={`w-9 h-9 rounded-full flex items-center justify-center text-xs font-bold ${isLive ? "bg-[hsl(var(--app-green)/0.15)] text-[hsl(var(--app-green))]" : "bg-[hsl(var(--app-surface-2))] text-[hsl(var(--app-text-muted))]"}`}>
                                {officer.name.split(" ").map(n => n[0]).join("").slice(0, 2).toUpperCase()}
                              </div>
                              <span className={`absolute -bottom-0.5 -right-0.5 w-3 h-3 rounded-full border-2 border-[hsl(var(--app-surface-1))] ${isLive ? "bg-[hsl(var(--app-green))]" : "bg-[hsl(var(--app-text-muted))]"}`} />
                            </div>
                            <div className="flex-1 min-w-0">
                              <p className="text-xs font-semibold text-[hsl(var(--app-text-primary))] truncate">{officer.name}</p>
                              <div className="flex items-center gap-1.5">
                                <span className={`px-1.5 py-0.5 rounded text-[9px] font-bold ${isLive ? "bg-[hsl(var(--app-green)/0.15)] text-[hsl(var(--app-green))]" : "bg-[hsl(var(--app-surface-2))] text-[hsl(var(--app-text-muted))]"}`}>
                                  {isLive ? "LIVE" : "○  OFFLINE"}
                                </span>
                                {siteName && <span className="text-[9px] text-[hsl(var(--app-text-muted))] truncate">{siteName}</span>}
                              </div>
                              {lastSeenAgo && (
                                <p className="text-[9px] text-[hsl(var(--app-text-muted))] mt-0.5 flex items-center gap-1">
                                  <Clock className="w-2.5 h-2.5" /> Last seen: {lastSeenAgo}
                                  {lastSeen && <span className="font-mono-data">({lastSeen.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })})</span>}
                                </p>
                              )}
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                  {filteredOfficers.length === 0 && <p className="text-xs text-[hsl(var(--app-text-muted))] italic">No officers found</p>}
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                  {filteredCheckpoints.map((cp) => (
                    <div key={cp.id} className={`app-card rounded-xl p-4 border-l-4 ${
                      cp.status === "done" ? "border-l-[hsl(var(--app-green))]" : cp.status === "due" ? "border-l-[hsl(var(--app-red))]" : "border-l-[hsl(var(--app-border-active))]"
                    }`}>
                      <div className="flex items-center justify-between mb-1">
                        <p className="text-sm font-semibold text-[hsl(var(--app-text-primary))]">{cp.name}</p>
                        <span className={`px-2 py-0.5 rounded-full text-[10px] font-semibold ${
                          cp.status === "done" ? "bg-[hsl(var(--app-green)/0.15)] text-[hsl(var(--app-green))]" :
                          cp.status === "due" ? "bg-[hsl(var(--app-red)/0.15)] text-[hsl(var(--app-red))]" :
                          "bg-[hsl(var(--app-border-subtle))] text-[hsl(var(--app-text-muted))]"
                        }`}>{cp.status === "done" ? "OK" : cp.status === "due" ? "DUE" : "Pending"}</span>
                      </div>
                      <p className="text-xs text-[hsl(var(--app-text-muted))]">{cp.zone}</p>
                      {cp.lastChecked ? (
                        <div className="mt-2 space-y-0.5">
                          <p className="text-xs text-[hsl(var(--app-text-secondary))]">Last: <span className="font-mono-data text-[hsl(var(--app-gold))]">{cp.lastChecked}</span> by <span className="font-semibold">{cp.lastOfficer}</span></p>
                          <p className="text-xs text-[hsl(var(--app-text-muted))] flex items-center gap-1"><Eye className="w-3 h-3 inline" /> {cp.lastMethod}</p>
                        </div>
                      ) : (
                        <p className="text-xs text-[hsl(var(--app-text-muted))] mt-2 italic">Not yet visited today</p>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* ========== SCHEDULE TAB ========== */}
            {activeTab === "schedule" && (
              <PatrolScheduler
                officers={officersList}
                sites={sites}
                checkpoints={dbCheckpoints}
                schedules={schedules}
                checkIns={checkIns}
                addSchedule={addScheduleMutation}
                updateSchedule={updateScheduleMutation}
                removeSchedule={removeScheduleMutation}
                generateRotation={generateRotationMutation}
              />
            )}

            {/* ========== LOG TAB ========== */}
            {activeTab === "log" && (
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <h2 className="font-bebas text-2xl text-[hsl(var(--app-text-primary))]">FULL CHECK-IN LOG</h2>
                  <div className="flex items-center gap-2">
                    <button onClick={() => { import("@/lib/pdfExport").then(m => m.exportLogPdf(filteredLog)); }} className="flex items-center gap-1 px-3 py-1.5 rounded-lg border border-[hsl(var(--app-border-subtle))] text-[hsl(var(--app-text-secondary))] text-xs hover:border-[hsl(var(--app-gold))] hover:text-[hsl(var(--app-gold))] transition-colors">
                      <Download className="w-3.5 h-3.5" /> Export PDF
                    </button>
                    <span className="px-2.5 py-1 rounded-full text-xs bg-[hsl(var(--app-gold)/0.15)] text-[hsl(var(--app-gold))] font-medium">{filteredLog.length} today</span>
                  </div>
                </div>
                {filteredLog.map((entry) => {
                  const lateReport = !entry.onTime ? lateReports.find(lr => lr.checkpoint === entry.checkpoint && lr.officer_id && officersList.find(o => o.id === lr.officer_id)?.name === entry.officer) : null;
                  const isExpanded = expandedLateLog === entry.id;
                  return (
                  <div key={entry.id} className="app-card rounded-xl p-4">
                    <div className="flex items-start justify-between">
                      <div className="space-y-1.5">
                        <div className="flex items-baseline gap-3">
                          <span className="font-mono-data text-[hsl(var(--app-gold))] text-sm font-semibold">{entry.time}</span>
                          <span className="text-sm font-semibold text-[hsl(var(--app-text-primary))]">{entry.checkpoint}</span>
                        </div>
                        <p className="text-xs text-[hsl(var(--app-text-muted))]">{entry.site}</p>
                        <p className="text-xs text-[hsl(var(--app-text-secondary))]">{entry.officer} · {entry.site}</p>
                        <p className="text-xs text-[hsl(var(--app-text-muted))] flex items-center gap-1"><Eye className="w-3 h-3" /> {entry.method} · <MapPin className="w-3 h-3" /> <span className="font-mono-data">{entry.coords}</span></p>
                        <div className="flex items-center gap-3">
                          <span className="text-xs text-[hsl(var(--app-text-muted))]">Scheduled {entry.scheduled}</span>
                          <span className={`text-xs font-semibold ${entry.onTime ? "text-[hsl(var(--app-green))]" : "text-[hsl(var(--app-gold))]"}`}>
                            {entry.onTime ? "On Time" : `Late (${entry.variance})`}
                          </span>
                        </div>
                      </div>
                      <div className="flex flex-col items-end gap-2">
                        <span className={`px-2 py-0.5 rounded-full text-[10px] font-semibold ${entry.onTime ? "bg-[hsl(var(--app-green)/0.15)] text-[hsl(var(--app-green))]" : "bg-[hsl(var(--app-gold)/0.15)] text-[hsl(var(--app-gold))]"}`}>
                          {entry.onTime ? "On Time" : "Late"}
                        </span>
                        {!entry.onTime && lateReport && (
                          <button
                            onClick={() => setExpandedLateLog(isExpanded ? null : entry.id)}
                            className="flex items-center gap-1 text-[10px] text-[hsl(var(--app-gold))] hover:underline"
                          >
                            <FileText className="w-3 h-3" /> View Reason {isExpanded ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
                          </button>
                        )}
                        {!entry.onTime && !lateReport && (
                          <span className="text-[10px] text-[hsl(var(--app-text-muted))] italic">No report submitted</span>
                        )}
                      </div>
                    </div>
                    {isExpanded && lateReport && (
                      <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: "auto", opacity: 1 }} exit={{ height: 0, opacity: 0 }} className="mt-3 pt-3 border-t border-[hsl(var(--app-border-subtle))]">
                        <div className="p-3 rounded-xl bg-[hsl(var(--app-gold)/0.06)] border border-[hsl(var(--app-gold)/0.15)] space-y-2">
                          <div className="flex items-center gap-2">
                            <AlertTriangle className="w-4 h-4 text-[hsl(var(--app-gold))]" />
                            <span className="text-xs font-semibold text-[hsl(var(--app-gold))]">Late Check-In Report — +{lateReport.late_minutes}min</span>
                          </div>
                          <div className="space-y-1">
                            <p className="text-xs text-[hsl(var(--app-text-secondary))]"><span className="font-semibold text-[hsl(var(--app-text-primary))]">Reason:</span> {lateReport.reason}</p>
                            {lateReport.details && (
                              <p className="text-xs text-[hsl(var(--app-text-secondary))]"><span className="font-semibold text-[hsl(var(--app-text-primary))]">Details:</span> {lateReport.details}</p>
                            )}
                            <p className="text-[10px] text-[hsl(var(--app-text-muted))] font-mono-data">Submitted {new Date(lateReport.created_at).toLocaleString()}</p>
                          </div>
                        </div>
                      </motion.div>
                    )}
                  </div>
                  );
                })}
                {filteredLog.length === 0 && <p className="text-center text-[hsl(var(--app-text-muted))] text-sm py-8">No check-ins recorded yet today</p>}
              </div>
            )}

            {/* ========== MAP TAB ========== */}
            {activeTab === "map" && (
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <div>
                    <h2 className="font-bebas text-2xl text-[hsl(var(--app-text-primary))]">LIVE OFFICER MAP</h2>
                    <p className="text-xs text-[hsl(var(--app-text-muted))]">Auto-refreshes every 15s · Showing {userLocation ? 'your location' : 'default view'}</p>
                  </div>
                </div>
                <MapAddressSearch mapInstance={mapInstanceRef.current} />
                <div ref={mapCallbackRef} className="w-full h-[480px] md:h-[480px] rounded-xl border border-[hsl(var(--app-gold)/0.3)] overflow-hidden bg-[hsl(var(--app-surface-2))]" />
                
                <div className="flex flex-wrap gap-4 text-xs text-[hsl(var(--app-text-secondary))]">
                  <span className="flex items-center gap-1.5"><span className="w-3 h-3 rounded-full bg-[hsl(var(--app-blue))]" /> You</span>
                  <span className="flex items-center gap-1.5"><span className="w-3 h-3 rounded-full bg-[hsl(var(--app-green))]" /> Officer (Live)</span>
                  <span className="flex items-center gap-1.5"><span className="w-3 h-3 rounded-full bg-gray-500" /> Officer (Offline)</span>
                  <span className="flex items-center gap-1.5"><span className="w-3 h-3 rounded-full bg-[hsl(var(--app-gold))]" /> Checkpoint (Visited)</span>
                  <span className="flex items-center gap-1.5"><span className="w-3 h-3 rounded-full bg-[hsl(var(--app-red))]" /> Checkpoint (Pending)</span>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  {filteredOfficers.map((officer) => (
                    <div key={officer.id} className="app-card rounded-xl p-4">
                      <div className="flex items-center justify-between mb-2">
                        <div>
                          <p className="text-sm font-semibold text-[hsl(var(--app-text-primary))]">{officer.name}</p>
                          <p className="text-xs text-[hsl(var(--app-text-muted))]">{getSiteName(officer.site_id)}</p>
                        </div>
                        <span className={`flex items-center gap-1.5 px-2 py-1 rounded-full text-[10px] font-semibold ${
                          isOfficerLive(officer) ? "bg-[hsl(var(--app-green)/0.15)] text-[hsl(var(--app-green))]" : "bg-[hsl(var(--app-border-subtle))] text-[hsl(var(--app-text-muted))]"
                        }`}>
                          <span className={`w-1.5 h-1.5 rounded-full ${isOfficerLive(officer) ? "bg-[hsl(var(--app-green))] animate-pulse-dot" : "bg-gray-500"}`} />
                          {isOfficerLive(officer) ? "LIVE" : "OFFLINE"}
                        </span>
                      </div>
                      <p className="text-xs text-[hsl(var(--app-text-muted))]">{getSiteName(officer.site_id)}</p>
                      <p className="text-xs font-mono-data text-[hsl(var(--app-text-muted))] mt-1 flex items-center gap-1"><MapPin className="w-3 h-3" /> {officer.lat?.toFixed(4) ?? '—'}, {officer.lng?.toFixed(4) ?? '—'}</p>
                      <div className="flex items-center justify-between mt-2">
                        <span className="text-xs text-[hsl(var(--app-text-muted))]">{officer.checkins} check-ins today</span>
                        <button onClick={() => { flyToOfficer(officer); }} className="text-xs text-[hsl(var(--app-gold))] hover:underline flex items-center gap-1"><MapPin className="w-3 h-3" /> Find on Map</button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* ========== INCIDENTS TAB ========== */}
            {activeTab === "incidents" && (
              <div className="space-y-4">
                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2">
                  <h2 className="font-bebas text-2xl text-[hsl(var(--app-text-primary))]">INCIDENTS</h2>
                  <div className="flex items-center gap-2">
                    <button onClick={() => { import("@/lib/pdfExport").then(m => m.exportIncidentsPdf(filteredIncidents)); }} className="flex items-center gap-1 px-3 py-1.5 rounded-lg border border-[hsl(var(--app-border-subtle))] text-[hsl(var(--app-text-secondary))] text-xs hover:border-[hsl(var(--app-gold))] hover:text-[hsl(var(--app-gold))] transition-colors">
                      <Download className="w-3.5 h-3.5" /> Export PDF
                    </button>
                    <button onClick={() => setShowAddIncidentModal(true)} className="flex items-center gap-1 px-3 py-1.5 rounded-lg bg-[hsl(var(--app-gold))] text-[hsl(var(--app-bg))] text-xs font-semibold hover:opacity-90 transition-opacity">
                      <Plus className="w-3.5 h-3.5" /> Report Incident
                    </button>
                  </div>
                </div>
                <div className="flex gap-2 overflow-x-auto pb-2">
                  {["all", "Low", "Medium", "High", "Critical", "Escalated"].map((f) => (
                    <button key={f} onClick={() => setIncidentFilter(f)} className={`px-3 py-1.5 rounded-full text-xs font-medium whitespace-nowrap transition-all ${
                      incidentFilter === f ? "bg-[hsl(var(--app-gold)/0.15)] text-[hsl(var(--app-gold))] border border-[hsl(var(--app-gold)/0.3)]" : "border border-[hsl(var(--app-border-subtle))] text-[hsl(var(--app-text-muted))]"
                    }`}>{f === "all" ? "All" : f}</button>
                  ))}
                </div>
                {filteredIncidents.map((inc) => (
                  <IncidentCard
                    key={inc.id}
                    incident={inc}
                    canResolve={true}
                    resolving={updateIncidentMutation.isPending}
                    onResolve={async (id, notes) => {
                      try {
                        await updateIncidentMutation.mutateAsync({ id, updates: { resolved: true, resolution_notes: notes || null } as any });
                        toast.success("Incident resolved");
                      } catch (err: any) {
                        toast.error(err.message || "Failed to resolve incident");
                      }
                    }}
                  />
                ))}
                {filteredIncidents.length === 0 && <p className="text-center text-[hsl(var(--app-text-muted))] text-sm py-8">No incidents reported</p>}

                {/* Add Incident Modal */}
                {showAddIncidentModal && (
                  <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60">
                    <div className="app-card rounded-2xl p-6 w-full max-w-md mx-4 space-y-4 relative max-h-[90vh] overflow-y-auto">
                      <button onClick={() => setShowAddIncidentModal(false)} className="absolute top-4 right-4 text-[hsl(var(--app-text-muted))] hover:text-[hsl(var(--app-text-primary))]"><X className="w-5 h-5" /></button>
                      <h3 className="font-bebas text-xl text-[hsl(var(--app-text-primary))]">REPORT INCIDENT</h3>
                      <div className="space-y-3">
                        <div>
                          <label className="text-xs text-[hsl(var(--app-text-muted))] mb-1 block">Title *</label>
                          <input value={newIncident.title} onChange={(e) => setNewIncident(p => ({ ...p, title: e.target.value }))} placeholder="Brief incident title" className="w-full rounded-lg border border-[hsl(var(--app-border-subtle))] bg-[hsl(var(--app-surface-2))] text-[hsl(var(--app-text-primary))] px-3 py-2 text-sm" />
                        </div>
                        <div>
                          <label className="text-xs text-[hsl(var(--app-text-muted))] mb-1 block">Description</label>
                          <textarea value={newIncident.description} onChange={(e) => setNewIncident(p => ({ ...p, description: e.target.value }))} placeholder="Describe the incident..." rows={3} className="w-full rounded-lg border border-[hsl(var(--app-border-subtle))] bg-[hsl(var(--app-surface-2))] text-[hsl(var(--app-text-primary))] px-3 py-2 text-sm resize-none" />
                        </div>
                        <div className="grid grid-cols-2 gap-3">
                          <div>
                            <label className="text-xs text-[hsl(var(--app-text-muted))] mb-1 block">Severity *</label>
                            <select value={newIncident.severity} onChange={(e) => setNewIncident(p => ({ ...p, severity: e.target.value }))} className="w-full rounded-lg border border-[hsl(var(--app-border-subtle))] bg-[hsl(var(--app-surface-2))] text-[hsl(var(--app-text-primary))] px-3 py-2 text-sm">
                              <option value="low">Low</option>
                              <option value="medium">Medium</option>
                              <option value="high">High</option>
                              <option value="critical">Critical</option>
                            </select>
                          </div>
                          <div>
                            <label className="text-xs text-[hsl(var(--app-text-muted))] mb-1 block">Category</label>
                            <select value={newIncident.category} onChange={(e) => setNewIncident(p => ({ ...p, category: e.target.value }))} className="w-full rounded-lg border border-[hsl(var(--app-border-subtle))] bg-[hsl(var(--app-surface-2))] text-[hsl(var(--app-text-primary))] px-3 py-2 text-sm">
                              <option value="Theft">Theft</option>
                              <option value="Trespassing">Trespassing</option>
                              <option value="Vandalism">Vandalism</option>
                              <option value="Fire">Fire</option>
                              <option value="Medical">Medical</option>
                              <option value="Suspicious Activity">Suspicious Activity</option>
                              <option value="Equipment Failure">Equipment Failure</option>
                              <option value="Other">Other</option>
                            </select>
                          </div>
                        </div>
                        <div>
                          <label className="text-xs text-[hsl(var(--app-text-muted))] mb-1 block">Site *</label>
                          <select value={newIncident.siteId} onChange={(e) => setNewIncident(p => ({ ...p, siteId: e.target.value }))} className="w-full rounded-lg border border-[hsl(var(--app-border-subtle))] bg-[hsl(var(--app-surface-2))] text-[hsl(var(--app-text-primary))] px-3 py-2 text-sm">
                            <option value="">Select site</option>
                            {sites.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
                          </select>
                        </div>
                        <div>
                          <label className="text-xs text-[hsl(var(--app-text-muted))] mb-1 block">Officer (optional)</label>
                          <select value={newIncident.officerId} onChange={(e) => setNewIncident(p => ({ ...p, officerId: e.target.value }))} className="w-full rounded-lg border border-[hsl(var(--app-border-subtle))] bg-[hsl(var(--app-surface-2))] text-[hsl(var(--app-text-primary))] px-3 py-2 text-sm">
                            <option value="">Select officer</option>
                            {officersList.filter(o => !newIncident.siteId || o.site_id === newIncident.siteId).map(o => <option key={o.id} value={o.id}>{o.name}</option>)}
                          </select>
                        </div>
                      </div>
                      <div className="flex gap-2 pt-2">
                        <button onClick={() => setShowAddIncidentModal(false)} className="flex-1 px-4 py-2 rounded-lg border border-[hsl(var(--app-border-subtle))] text-[hsl(var(--app-text-muted))] text-sm hover:bg-[hsl(var(--app-surface-2))]">Cancel</button>
                        <button onClick={handleAddIncident} disabled={addIncidentMutation.isPending} className="flex-1 px-4 py-2 rounded-lg bg-[hsl(var(--app-gold))] text-[hsl(var(--app-bg))] text-sm font-semibold hover:opacity-90 disabled:opacity-50">
                          {addIncidentMutation.isPending ? "Submitting..." : "Report Incident"}
                        </button>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* ========== RISKS TAB ========== */}
            {activeTab === "risks" && (
              <RiskManagement
                sites={sites.map(s => ({ id: s.id, name: s.name }))}
                mode="business_owner"
              />
            )}

            {/* ========== FACE LOG TAB ========== */}
            {activeTab === "facelog" && (
              <div className="space-y-4">
                <h2 className="font-bebas text-2xl text-[hsl(var(--app-text-primary))]">FACE VERIFICATION LOGS</h2>
                <FaceVerificationLogs sites={sites.map(s => ({ id: s.id, name: s.name }))} />
              </div>
            )}


            {activeTab === "officers" && (
              <div className="space-y-4">
                <div className="flex items-center justify-between flex-wrap gap-2">
                  <div>
                    <h2 className="font-bebas text-2xl text-[hsl(var(--app-text-primary))]">OFFICERS</h2>
                    <p className="text-xs text-[hsl(var(--app-text-muted))]">{filteredOfficers.length} officers · {filteredOfficers.filter(o => isOfficerLive(o)).length} active now</p>
                  </div>
                  <div className="flex flex-wrap items-center gap-2">
                    <button onClick={() => setShowAutoScheduleModal(true)} className="flex-1 min-w-[140px] px-3 py-2 rounded-xl border border-[hsl(var(--app-border-active))] text-xs font-medium text-[hsl(var(--app-text-secondary))] hover:border-[hsl(var(--app-gold))] hover:text-[hsl(var(--app-gold))] transition-colors flex items-center justify-center gap-1.5 whitespace-nowrap">
                      <Clock className="w-3.5 h-3.5" /> Auto Schedule
                      {autoSchedule.enabled && <span className="w-1.5 h-1.5 rounded-full bg-[hsl(var(--app-green))]" />}
                    </button>
                    <button onClick={handleIssueAllPasswords} className="flex-1 min-w-[140px] px-3 py-2 rounded-xl border border-[hsl(var(--app-gold)/0.3)] text-xs font-medium text-[hsl(var(--app-gold))] hover:bg-[hsl(var(--app-gold)/0.1)] transition-colors flex items-center justify-center gap-1.5 whitespace-nowrap">
                      <Key className="w-3.5 h-3.5" /> Issue All
                    </button>
                    <button onClick={() => csvFileRef.current?.click()} className="flex-1 min-w-[130px] px-3 py-2 rounded-xl border border-[hsl(var(--app-border-active))] text-xs font-medium text-[hsl(var(--app-text-secondary))] hover:border-[hsl(var(--app-gold))] hover:text-[hsl(var(--app-gold))] transition-colors flex items-center justify-center gap-1.5 whitespace-nowrap">
                      <Upload className="w-3.5 h-3.5" /> CSV Import
                    </button>
                    <input ref={csvFileRef} type="file" accept=".csv" onChange={handleCsvFileChange} className="hidden" />
                    <button onClick={() => setShowAddOfficerModal(true)} className="flex-1 min-w-[140px] px-4 py-2 rounded-xl bg-[hsl(var(--app-gold))] text-[hsl(var(--app-bg))] text-sm font-semibold hover:brightness-110 transition-all flex items-center justify-center gap-1.5 whitespace-nowrap">
                      <Plus className="w-4 h-4" /> Add Officer
                    </button>
                  </div>
                </div>

                {/* Auto-schedule info bar */}
                {autoSchedule.enabled && (
                  <div className="flex items-center gap-3 p-3 rounded-xl bg-[hsl(var(--app-green)/0.06)] border border-[hsl(var(--app-green)/0.2)]">
                    <Clock className="w-4 h-4 text-[hsl(var(--app-green))]" />
                    <p className="text-xs text-[hsl(var(--app-green))]">
                      Auto-generating shift passwords daily at <span className="font-semibold font-mono-data">{autoSchedule.time}</span> · Expires after {autoSchedule.expiryHours}h
                    </p>
                  </div>
                )}

                {/* Auto Schedule Modal */}
                <AnimatePresence>
                  {showAutoScheduleModal && (
                    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="fixed inset-0 z-50 flex items-center justify-center bg-[hsl(var(--app-bg)/0.95)] backdrop-blur-sm">
                      <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} exit={{ opacity: 0, scale: 0.95 }} className="app-card rounded-2xl p-6 max-w-md w-full mx-4">
                        <div className="flex items-center justify-between mb-5">
                          <h3 className="font-bebas text-xl text-[hsl(var(--app-text-primary))]">AUTO PASSWORD SCHEDULE</h3>
                          <button onClick={() => setShowAutoScheduleModal(false)} className="p-1.5 rounded-lg text-[hsl(var(--app-text-muted))] hover:text-[hsl(var(--app-text-secondary))] hover:bg-[hsl(var(--app-surface-2))] transition-colors">
                            <X className="w-4 h-4" />
                          </button>
                        </div>
                        <div className="space-y-4">
                          <p className="text-xs text-[hsl(var(--app-text-secondary))]">
                            Set a time for the system to automatically generate and distribute shift passwords to all officers daily.
                          </p>
                          <div className="flex items-center justify-between p-4 rounded-xl bg-[hsl(var(--app-surface-2))]">
                            <div>
                              <p className="text-sm font-medium text-[hsl(var(--app-text-primary))]">Enable Auto-Generation</p>
                              <p className="text-[10px] text-[hsl(var(--app-text-muted))]">Passwords generated & sent automatically</p>
                            </div>
                            <button
                              onClick={() => setScheduleForm(p => ({ ...p, enabled: !p.enabled }))}
                              className={`w-12 h-6 rounded-full transition-colors relative ${scheduleForm.enabled ? "bg-[hsl(var(--app-gold))]" : "bg-[hsl(var(--app-border-subtle))]"}`}
                            >
                              <div className={`absolute top-1 w-4 h-4 rounded-full bg-white shadow transition-transform ${scheduleForm.enabled ? "translate-x-7" : "translate-x-1"}`} />
                            </button>
                          </div>
                          {scheduleForm.enabled && (
                            <>
                              <div>
                                <label className="text-[10px] font-semibold text-[hsl(var(--app-text-muted))] uppercase tracking-wider mb-1 block">Generation Time</label>
                                <input
                                  type="time"
                                  value={scheduleForm.time}
                                  onChange={e => setScheduleForm(p => ({ ...p, time: e.target.value }))}
                                  className="w-full px-4 py-3 rounded-xl border border-[hsl(var(--app-border-subtle))] bg-[hsl(var(--app-surface-2))] text-sm text-[hsl(var(--app-text-primary))] font-mono-data outline-none focus:border-[hsl(var(--app-gold))] transition-colors"
                                />
                              </div>
                              <div>
                                <label className="text-[10px] font-semibold text-[hsl(var(--app-text-muted))] uppercase tracking-wider mb-1 block">Password Expiry (hours)</label>
                                <div className="flex gap-2">
                                  {[8, 12, 16, 24].map(h => (
                                    <button
                                      key={h}
                                      onClick={() => setScheduleForm(p => ({ ...p, expiryHours: h }))}
                                      className={`flex-1 py-2.5 rounded-xl text-xs font-medium transition-all ${
                                        scheduleForm.expiryHours === h
                                          ? "bg-[hsl(var(--app-gold)/0.15)] text-[hsl(var(--app-gold))] border border-[hsl(var(--app-gold)/0.3)]"
                                          : "border border-[hsl(var(--app-border-subtle))] text-[hsl(var(--app-text-muted))]"
                                      }`}
                                    >
                                      {h}h
                                    </button>
                                  ))}
                                </div>
                              </div>
                            </>
                          )}
                          <div className="flex gap-3 mt-2">
                            <button onClick={() => setShowAutoScheduleModal(false)} className="flex-1 py-3 rounded-xl border border-[hsl(var(--app-border-subtle))] text-sm text-[hsl(var(--app-text-muted))] hover:bg-[hsl(var(--app-surface-2))] transition-colors">Cancel</button>
                            <button onClick={handleSaveAutoSchedule} className="flex-1 py-3 rounded-xl bg-[hsl(var(--app-gold))] text-[hsl(var(--app-bg))] text-sm font-semibold hover:brightness-110 transition-all">Save Schedule</button>
                          </div>
                        </div>
                      </motion.div>
                    </motion.div>
                  )}
                </AnimatePresence>

                {/* Add Officer Modal */}
                <AnimatePresence>
                  {showAddOfficerModal && (
                    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="fixed inset-0 z-50 flex items-center justify-center bg-[hsl(var(--app-bg)/0.95)] backdrop-blur-sm">
                      <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} exit={{ opacity: 0, scale: 0.95 }} className="app-card rounded-2xl p-6 max-w-md w-full mx-4">
                        <div className="flex items-center justify-between mb-5">
                          <h3 className="font-bebas text-xl text-[hsl(var(--app-text-primary))]">ADD NEW OFFICER</h3>
                          <button onClick={() => setShowAddOfficerModal(false)} className="p-1.5 rounded-lg text-[hsl(var(--app-text-muted))] hover:text-[hsl(var(--app-text-secondary))] hover:bg-[hsl(var(--app-surface-2))] transition-colors">
                            <X className="w-4 h-4" />
                          </button>
                        </div>
                        <div className="space-y-3">
                          <div className="grid grid-cols-2 gap-3">
                            <div>
                              <label className="text-[10px] font-semibold text-[hsl(var(--app-text-muted))] uppercase tracking-wider mb-1 block">Full Name *</label>
                              <input value={newOfficerForm.name} onChange={e => setNewOfficerForm(p => ({ ...p, name: e.target.value }))} placeholder="e.g. Sipho Nkosi" className="w-full px-4 py-3 rounded-xl border border-[hsl(var(--app-border-subtle))] bg-[hsl(var(--app-surface-2))] text-sm text-[hsl(var(--app-text-primary))] placeholder:text-[hsl(var(--app-text-muted))] outline-none focus:border-[hsl(var(--app-gold))] transition-colors" />
                            </div>
                            <div>
                              <label className="text-[10px] font-semibold text-[hsl(var(--app-text-muted))] uppercase tracking-wider mb-1 block">Username * <span className="normal-case text-[hsl(var(--app-text-muted))]">(login)</span></label>
                              <input value={newOfficerForm.username} onChange={e => setNewOfficerForm(p => ({ ...p, username: e.target.value.toLowerCase().replace(/\s/g, '') }))} placeholder="e.g. sipho.nkosi" className="w-full px-4 py-3 rounded-xl border border-[hsl(var(--app-border-subtle))] bg-[hsl(var(--app-surface-2))] text-sm text-[hsl(var(--app-text-primary))] placeholder:text-[hsl(var(--app-text-muted))] outline-none focus:border-[hsl(var(--app-gold))] transition-colors" />
                            </div>
                          </div>
                          <div className="grid grid-cols-2 gap-3">
                            <div>
                              <label className="text-[10px] font-semibold text-[hsl(var(--app-text-muted))] uppercase tracking-wider mb-1 block">Phone Number</label>
                              <input value={newOfficerForm.phone} onChange={e => setNewOfficerForm(p => ({ ...p, phone: e.target.value }))} placeholder="e.g. 071 234 5678" type="tel" className="w-full px-4 py-3 rounded-xl border border-[hsl(var(--app-border-subtle))] bg-[hsl(var(--app-surface-2))] text-sm text-[hsl(var(--app-text-primary))] placeholder:text-[hsl(var(--app-text-muted))] outline-none focus:border-[hsl(var(--app-gold))] transition-colors" />
                            </div>
                            <div>
                              <label className="text-[10px] font-semibold text-[hsl(var(--app-text-muted))] uppercase tracking-wider mb-1 block">Email <span className="normal-case text-[hsl(var(--app-text-muted))]">(optional)</span></label>
                              <input value={newOfficerForm.email} onChange={e => setNewOfficerForm(p => ({ ...p, email: e.target.value }))} placeholder="e.g. officer@email.com" type="email" className="w-full px-4 py-3 rounded-xl border border-[hsl(var(--app-border-subtle))] bg-[hsl(var(--app-surface-2))] text-sm text-[hsl(var(--app-text-primary))] placeholder:text-[hsl(var(--app-text-muted))] outline-none focus:border-[hsl(var(--app-gold))] transition-colors" />
                            </div>
                          </div>
                          <div>
                            <label className="text-[10px] font-semibold text-[hsl(var(--app-text-muted))] uppercase tracking-wider mb-1 block">Assign to Site *</label>
                            <select value={newOfficerForm.siteId} onChange={e => setNewOfficerForm(p => ({ ...p, siteId: e.target.value }))} className="w-full px-4 py-3 rounded-xl border border-[hsl(var(--app-border-subtle))] bg-[hsl(var(--app-surface-2))] text-sm text-[hsl(var(--app-text-primary))] outline-none focus:border-[hsl(var(--app-gold))] transition-colors">
                              <option value="">Select a site...</option>
                              {sites.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
                            </select>
                          </div>
                          <div className="flex gap-3 mt-2">
                            <button onClick={() => setShowAddOfficerModal(false)} className="flex-1 py-3 rounded-xl border border-[hsl(var(--app-border-subtle))] text-sm text-[hsl(var(--app-text-muted))] hover:bg-[hsl(var(--app-surface-2))] transition-colors">Cancel</button>
                            <button onClick={handleAddNewOfficer} className="flex-1 py-3 rounded-xl bg-[hsl(var(--app-gold))] text-[hsl(var(--app-bg))] text-sm font-semibold hover:brightness-110 transition-all flex items-center justify-center gap-1.5">
                              <Plus className="w-4 h-4" /> Add Officer
                            </button>
                          </div>
                        </div>
                      </motion.div>
                    </motion.div>
                  )}
                </AnimatePresence>

                {/* Password Issued Modal */}
                <AnimatePresence>
                  {showPasswordModal && issuedPassword && (
                    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="fixed inset-0 z-50 flex items-center justify-center bg-[hsl(var(--app-bg)/0.95)] backdrop-blur-sm">
                      <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} exit={{ opacity: 0, scale: 0.95 }} className="app-card rounded-2xl p-6 max-w-sm w-full mx-4 text-center">
                        <div className="w-14 h-14 rounded-full bg-[hsl(var(--app-green)/0.12)] flex items-center justify-center mx-auto mb-4">
                          <Key className="w-7 h-7 text-[hsl(var(--app-green))]" />
                        </div>
                        <h3 className="font-bebas text-xl text-[hsl(var(--app-text-primary))]">PASSWORD ISSUED</h3>
                        <p className="text-xs text-[hsl(var(--app-text-muted))] mt-1">
                          For {officersList.find(o => o.id === showPasswordModal)?.name}
                        </p>
                        <div className="mt-4 p-4 rounded-xl bg-[hsl(var(--app-surface-2))] border border-[hsl(var(--app-gold)/0.3)]">
                          <p className="font-mono-data text-2xl font-bold text-[hsl(var(--app-gold))] tracking-[0.2em]">{issuedPassword.password}</p>
                        </div>
                        <div className="mt-3 text-xs text-[hsl(var(--app-text-muted))] space-y-1">
                          <p>Issued: {new Date(issuedPassword.issued_at).toLocaleTimeString()}</p>
                          <p>Expires: {new Date(issuedPassword.expires_at).toLocaleTimeString()}</p>
                        </div>
                        <div className="flex gap-3 mt-5">
                          <button
                            onClick={() => {
                              navigator.clipboard.writeText(issuedPassword.password);
                              toast.success("Password copied to clipboard");
                            }}
                            className="flex-1 py-3 rounded-xl border border-[hsl(var(--app-gold)/0.3)] text-sm font-medium text-[hsl(var(--app-gold))] hover:bg-[hsl(var(--app-gold)/0.1)] transition-colors flex items-center justify-center gap-1.5"
                          >
                            <Copy className="w-4 h-4" /> Copy
                          </button>
                          <button onClick={() => { setShowPasswordModal(null); setIssuedPassword(null); }} className="flex-1 py-3 rounded-xl bg-[hsl(var(--app-gold))] text-[hsl(var(--app-bg))] text-sm font-semibold hover:brightness-110 transition-all">Done</button>
                        </div>
                      </motion.div>
                    </motion.div>
                  )}
                </AnimatePresence>

                {/* Temp Login Modal */}
                <AnimatePresence>
                  {tempLoginResult && (
                    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="fixed inset-0 z-50 flex items-center justify-center bg-[hsl(var(--app-bg)/0.95)] backdrop-blur-sm">
                      <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} exit={{ opacity: 0, scale: 0.95 }} className="app-card rounded-2xl p-6 max-w-sm w-full mx-4 text-center">
                        <div className="w-14 h-14 rounded-full bg-[hsl(var(--app-gold)/0.12)] flex items-center justify-center mx-auto mb-4">
                          <Key className="w-7 h-7 text-[hsl(var(--app-gold))]" />
                        </div>
                        <h3 className="font-bebas text-xl text-[hsl(var(--app-text-primary))]">TEMPORARY LOGIN</h3>
                        <p className="text-xs text-[hsl(var(--app-text-muted))] mt-1">
                          Share these credentials with {tempLoginResult.officerName}
                        </p>
                        <div className="mt-4 space-y-3">
                          <div className="p-3 rounded-xl bg-[hsl(var(--app-surface-2))] border border-[hsl(var(--app-border-subtle))] text-left">
                            <p className="text-[10px] font-semibold text-[hsl(var(--app-text-muted))] uppercase tracking-wider mb-1">Username</p>
                            <div className="flex items-center justify-between">
                              <span className="font-mono-data text-sm font-bold text-[hsl(var(--app-text-primary))]">{tempLoginResult.username}</span>
                              <button onClick={() => { navigator.clipboard.writeText(tempLoginResult.username); toast.success("Username copied"); }} className="p-1 rounded-lg hover:bg-[hsl(var(--app-surface-2))] transition-colors">
                                <Copy className="w-3.5 h-3.5 text-[hsl(var(--app-text-muted))]" />
                              </button>
                            </div>
                          </div>
                          <div className="p-3 rounded-xl bg-[hsl(var(--app-surface-2))] border border-[hsl(var(--app-gold)/0.3)] text-left">
                            <p className="text-[10px] font-semibold text-[hsl(var(--app-text-muted))] uppercase tracking-wider mb-1">Temporary Password</p>
                            <div className="flex items-center justify-between">
                              <span className="font-mono-data text-lg font-bold text-[hsl(var(--app-gold))] tracking-[0.15em]">{tempLoginResult.tempPassword}</span>
                              <button onClick={() => { navigator.clipboard.writeText(tempLoginResult.tempPassword); toast.success("Password copied"); }} className="p-1 rounded-lg hover:bg-[hsl(var(--app-surface-2))] transition-colors">
                                <Copy className="w-3.5 h-3.5 text-[hsl(var(--app-text-secondary))]" />
                              </button>
                            </div>
                          </div>
                        </div>
                        <p className="text-[10px] text-[hsl(var(--app-text-muted))] mt-3">The officer can use these to log in immediately. The password will be replaced on next shift password issue.</p>
                        <div className="flex gap-3 mt-5">
                          <button
                            onClick={() => {
                              navigator.clipboard.writeText(`Username: ${tempLoginResult.username}\nPassword: ${tempLoginResult.tempPassword}`);
                              toast.success("Login details copied");
                            }}
                            className="flex-1 py-3 rounded-xl border border-[hsl(var(--app-gold)/0.3)] text-sm font-medium text-[hsl(var(--app-gold))] hover:bg-[hsl(var(--app-gold)/0.1)] transition-colors flex items-center justify-center gap-1.5"
                          >
                            <Copy className="w-4 h-4" /> Copy All
                          </button>
                          <button onClick={() => setTempLoginResult(null)} className="flex-1 py-3 rounded-xl bg-[hsl(var(--app-gold))] text-[hsl(var(--app-bg))] text-sm font-semibold hover:brightness-110 transition-all">Done</button>
                        </div>
                      </motion.div>
                    </motion.div>
                  )}
                </AnimatePresence>

                <AnimatePresence>
                  {editOfficer && (
                    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="fixed inset-0 z-50 flex items-center justify-center bg-[hsl(var(--app-bg)/0.95)] backdrop-blur-sm">
                      <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} exit={{ opacity: 0, scale: 0.95 }} className="app-card rounded-2xl p-6 max-w-md w-full mx-4">
                        <div className="flex items-center justify-between mb-5">
                          <h3 className="font-bebas text-xl text-[hsl(var(--app-text-primary))]">EDIT OFFICER</h3>
                          <button onClick={() => setEditOfficer(null)} className="p-1.5 rounded-lg text-[hsl(var(--app-text-muted))] hover:text-[hsl(var(--app-text-secondary))] hover:bg-[hsl(var(--app-surface-2))] transition-colors">
                            <X className="w-4 h-4" />
                          </button>
                        </div>
                        <div className="space-y-3">
                          <div className="grid grid-cols-2 gap-3">
                            <div>
                              <label className="text-[10px] font-semibold text-[hsl(var(--app-text-muted))] uppercase tracking-wider mb-1 block">Full Name *</label>
                              <input value={editForm.name} onChange={e => setEditForm(p => ({ ...p, name: e.target.value }))} className="w-full px-4 py-3 rounded-xl border border-[hsl(var(--app-border-subtle))] bg-[hsl(var(--app-surface-2))] text-sm text-[hsl(var(--app-text-primary))] outline-none focus:border-[hsl(var(--app-gold))] transition-colors" />
                            </div>
                            <div>
                              <label className="text-[10px] font-semibold text-[hsl(var(--app-text-muted))] uppercase tracking-wider mb-1 block">Phone Number</label>
                              <input value={editForm.phone} onChange={e => setEditForm(p => ({ ...p, phone: e.target.value }))} type="tel" className="w-full px-4 py-3 rounded-xl border border-[hsl(var(--app-border-subtle))] bg-[hsl(var(--app-surface-2))] text-sm text-[hsl(var(--app-text-primary))] outline-none focus:border-[hsl(var(--app-gold))] transition-colors" />
                            </div>
                          </div>
                          <div>
                            <label className="text-[10px] font-semibold text-[hsl(var(--app-text-muted))] uppercase tracking-wider mb-1 block">Email</label>
                            <input value={editForm.email} onChange={e => setEditForm(p => ({ ...p, email: e.target.value }))} type="email" className="w-full px-4 py-3 rounded-xl border border-[hsl(var(--app-border-subtle))] bg-[hsl(var(--app-surface-2))] text-sm text-[hsl(var(--app-text-primary))] outline-none focus:border-[hsl(var(--app-gold))] transition-colors" />
                          </div>
                          <div>
                            <label className="text-[10px] font-semibold text-[hsl(var(--app-text-muted))] uppercase tracking-wider mb-1 block">Username (Login)</label>
                            <input value={editForm.username} onChange={e => setEditForm(p => ({ ...p, username: e.target.value }))} placeholder="e.g. john.doe" className="w-full px-4 py-3 rounded-xl border border-[hsl(var(--app-border-subtle))] bg-[hsl(var(--app-surface-2))] text-sm text-[hsl(var(--app-text-primary))] outline-none focus:border-[hsl(var(--app-gold))] transition-colors" />
                          </div>
                          <div>
                            <label className="text-[10px] font-semibold text-[hsl(var(--app-text-muted))] uppercase tracking-wider mb-1 block">Assigned Site *</label>
                            <select value={editForm.siteId} onChange={e => setEditForm(p => ({ ...p, siteId: e.target.value }))} className="w-full px-4 py-3 rounded-xl border border-[hsl(var(--app-border-subtle))] bg-[hsl(var(--app-surface-2))] text-sm text-[hsl(var(--app-text-primary))] outline-none focus:border-[hsl(var(--app-gold))] transition-colors">
                              <option value="">Select a site...</option>
                              {sites.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
                            </select>
                            {editForm.siteId !== editOfficer.site_id && editForm.siteId && (
                              <p className="text-[10px] text-[hsl(var(--app-gold))] mt-1 flex items-center gap-1">
                                <MapPin className="w-3 h-3" /> Officer will be reassigned to {sites.find(s => s.id === editForm.siteId)?.name}
                              </p>
                            )}
                          </div>
                          
                            <div className="p-3 rounded-xl border border-[hsl(var(--app-border-subtle))] bg-[hsl(var(--app-surface-2))] space-y-2">
                              {resetPasswordResult && resetPasswordResult.officerName === editOfficer.name ? (
                                <div className="space-y-2">
                                  <p className="text-[10px] font-semibold text-[hsl(var(--app-text-muted))] uppercase tracking-wider">New Temporary Password</p>
                                  <div className="flex items-center justify-between">
                                    <span className="font-mono text-lg font-bold text-[hsl(var(--app-gold))] tracking-[2px]">{resetPasswordResult.tempPassword}</span>
                                    <button onClick={() => { navigator.clipboard.writeText(resetPasswordResult.tempPassword); toast.success("Password copied"); }} className="p-1.5 rounded-lg border border-[hsl(var(--app-border-subtle))] hover:border-[hsl(var(--app-gold))] transition-colors">
                                      <Copy className="w-3.5 h-3.5 text-[hsl(var(--app-text-secondary))]" />
                                    </button>
                                  </div>
                                  <p className="text-[10px] text-[hsl(var(--app-text-muted))]">Share this with the officer. They'll use it with their username to sign in.</p>
                                </div>
                              ) : (
                                <div className="flex gap-2">
                                  <button
                                    onClick={() => handleResetOfficerPassword(editOfficer.id)}
                                    disabled={resetPasswordLoading}
                                    className="flex-1 py-2.5 rounded-lg border border-red-500/30 text-red-400 text-[10px] font-semibold hover:bg-red-500/10 transition-colors flex items-center justify-center gap-1.5 disabled:opacity-50"
                                  >
                                    <Key className="w-3 h-3" />
                                    {resetPasswordLoading ? "Resetting..." : "Reset Password"}
                                  </button>
                                  <button
                                    onClick={() => handleManualLogin(editOfficer.id, editOfficer.name)}
                                    disabled={manualLoginLoading === editOfficer.id}
                                    className="flex-1 py-2.5 rounded-lg border border-[hsl(var(--app-gold)/0.3)] text-[hsl(var(--app-gold))] text-[10px] font-semibold hover:bg-[hsl(var(--app-gold)/0.1)] transition-colors flex items-center justify-center gap-1.5 disabled:opacity-50"
                                  >
                                    <ShieldCheck className="w-3 h-3" />
                                    {manualLoginLoading === editOfficer.id ? "Logging in..." : "Force Login"}
                                  </button>
                                </div>
                              )}
                            </div>

                          <div className="flex gap-3 mt-2">
                            <button onClick={() => { setEditOfficer(null); setResetPasswordResult(null); }} className="flex-1 py-3 rounded-xl border border-[hsl(var(--app-border-subtle))] text-sm text-[hsl(var(--app-text-muted))] hover:bg-[hsl(var(--app-surface-2))] transition-colors">Cancel</button>
                            <button onClick={handleSaveEdit} className="flex-1 py-3 rounded-xl bg-[hsl(var(--app-gold))] text-[hsl(var(--app-bg))] text-sm font-semibold hover:brightness-110 transition-all flex items-center justify-center gap-1.5">
                              <CheckCircle2 className="w-4 h-4" /> Save Changes
                            </button>
                          </div>
                        </div>
                      </motion.div>
                    </motion.div>
                  )}
                </AnimatePresence>

                {/* CSV Import Modal */}
                <AnimatePresence>
                  {showCsvImportModal && (
                    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="fixed inset-0 z-50 flex items-center justify-center bg-[hsl(var(--app-bg)/0.95)] backdrop-blur-sm">
                      <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} exit={{ opacity: 0, scale: 0.95 }} className="app-card rounded-2xl p-6 max-w-lg w-full mx-4 max-h-[80vh] flex flex-col">
                        <div className="flex items-center justify-between mb-5">
                          <div>
                            <h3 className="font-bebas text-xl text-[hsl(var(--app-text-primary))]">CSV IMPORT PREVIEW</h3>
                            <p className="text-xs text-[hsl(var(--app-text-muted))]">{csvParsedOfficers.filter(r => r.valid).length} valid · {csvParsedOfficers.filter(r => !r.valid).length} errors</p>
                          </div>
                          <button onClick={() => { setShowCsvImportModal(false); setCsvParsedOfficers([]); }} className="p-1.5 rounded-lg text-[hsl(var(--app-text-muted))] hover:text-[hsl(var(--app-text-secondary))] hover:bg-[hsl(var(--app-surface-2))] transition-colors">
                            <X className="w-4 h-4" />
                          </button>
                        </div>

                        <div className="flex-1 overflow-y-auto space-y-2 mb-4 pr-1">
                          {csvParsedOfficers.map((row, i) => (
                            <div key={i} className={`p-3 rounded-xl border ${row.valid ? "border-[hsl(var(--app-green)/0.2)] bg-[hsl(var(--app-green)/0.04)]" : "border-[hsl(var(--app-red)/0.2)] bg-[hsl(var(--app-red)/0.04)]"}`}>
                              <div className="flex items-center justify-between">
                                <div>
                                  <p className="text-sm font-medium text-[hsl(var(--app-text-primary))]">{row.name || "—"}</p>
                                  <p className="text-xs text-[hsl(var(--app-text-muted))]">{row.phone || row.email || "—"}</p>
                                </div>
                                {row.valid ? (
                                  <CheckCircle2 className="w-4 h-4 text-[hsl(var(--app-green))]" />
                                ) : (
                                  <AlertTriangle className="w-4 h-4 text-[hsl(var(--app-red))]" />
                                )}
                              </div>
                              <div className="flex gap-3 mt-1 text-[10px] text-[hsl(var(--app-text-muted))]">
                                {row.phone && <span>{row.phone}</span>}
                                {row.email && <span>{row.email}</span>}
                                {row.siteName && <span>ðŸ“ {row.siteName}</span>}
                              </div>
                              {!row.valid && <p className="text-[10px] text-[hsl(var(--app-red))] mt-1">{row.error}</p>}
                            </div>
                          ))}
                        </div>

                        <div className="p-3 rounded-xl bg-[hsl(var(--app-surface-2))] mb-4">
                          <p className="text-[10px] text-[hsl(var(--app-text-muted))]">
                            <span className="font-semibold text-[hsl(var(--app-text-secondary))]">CSV format:</span> name, phone, email, site — Header row required. Site names must match existing sites exactly.
                          </p>
                        </div>

                        <div className="flex gap-3">
                          <button onClick={() => { setShowCsvImportModal(false); setCsvParsedOfficers([]); }} className="flex-1 py-3 rounded-xl border border-[hsl(var(--app-border-subtle))] text-sm text-[hsl(var(--app-text-muted))] hover:bg-[hsl(var(--app-surface-2))] transition-colors">Cancel</button>
                          <button onClick={handleCsvImport} disabled={csvImporting || csvParsedOfficers.filter(r => r.valid).length === 0} className="flex-1 py-3 rounded-xl bg-[hsl(var(--app-gold))] text-[hsl(var(--app-bg))] text-sm font-semibold hover:brightness-110 transition-all flex items-center justify-center gap-1.5 disabled:opacity-50">
                            <Upload className="w-4 h-4" /> Import {csvParsedOfficers.filter(r => r.valid).length} Officers
                          </button>
                        </div>
                      </motion.div>
                    </motion.div>
                  )}
                </AnimatePresence>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  {filteredOfficers.map((officer) => {
                    const pwd = shiftPasswords[officer.id];
                    const hasValidPwd = pwd && new Date(pwd.expires_at) > new Date();
                    return (
                    <div key={officer.id} className="app-card rounded-xl p-5">
                      <div className="flex items-center justify-between mb-3">
                        <div className="flex items-center gap-3">
                          {/* Face photo thumbnail */}
                          <div className="relative shrink-0">
                            {officer.face_photo_url ? (
                              <img src={officer.face_photo_url} alt={officer.name} className="w-10 h-10 rounded-full object-cover border-2 border-[hsl(var(--app-green)/0.3)]" />
                            ) : (
                              <div className="w-10 h-10 rounded-full bg-[hsl(var(--app-surface-2))] border-2 border-dashed border-[hsl(var(--app-border-active))] flex items-center justify-center">
                                <ScanFace className="w-5 h-5 text-[hsl(var(--app-text-muted)/0.4)]" />
                              </div>
                            )}
                          </div>
                          <div>
                            <p className="text-base font-semibold text-[hsl(var(--app-text-primary))]">{officer.name}</p>
                            <p className="text-xs text-[hsl(var(--app-text-muted))]">{getSiteName(officer.site_id)}</p>
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          <span className={`px-2 py-1 rounded-full text-[10px] font-semibold ${
                            isOfficerLive(officer) ? "bg-[hsl(var(--app-green)/0.15)] text-[hsl(var(--app-green))]" : "bg-[hsl(var(--app-border-subtle))] text-[hsl(var(--app-text-muted))]"
                          }`}>{isOfficerLive(officer) ? "Active" : "Offline"}</span>
                          <button onClick={() => handleRemoveOfficer(officer.id)} className="p-1.5 rounded-lg text-[hsl(var(--app-red)/0.4)] hover:bg-[hsl(var(--app-red)/0.1)] hover:text-[hsl(var(--app-red))] transition-colors">
                            <Trash2 className="w-3.5 h-3.5" />
                          </button>
                        </div>
                      </div>
                      <p className="text-xs text-[hsl(var(--app-text-muted))]">{getSiteName(officer.site_id)}</p>
                      
                      {/* Password status */}
                      {hasValidPwd && (
                        <div className="mt-2 flex items-center gap-2 p-2 rounded-lg bg-[hsl(var(--app-green)/0.06)] border border-[hsl(var(--app-green)/0.15)]">
                          <Key className="w-3 h-3 text-[hsl(var(--app-green))]" />
                          <span className="text-[10px] text-[hsl(var(--app-green))] font-medium">Password active · Expires {new Date(pwd.expires_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}</span>
                        </div>
                      )}
                      
                      {(() => {
                        const todaySchedules = schedules.filter((s: any) => s.officer_id === officer.id);
                        const todayDone = todaySchedules.filter((s: any) => s.status === "completed").length;
                        const todayCheckInsForOfficer = checkIns.filter((c: any) => c.officer_id === officer.id);
                        const completedCount = Math.max(todayDone, todayCheckInsForOfficer.length);
                        const total = Math.max(todaySchedules.length, 1);
                        const coveragePct = todaySchedules.length > 0 ? Math.min(100, Math.round((completedCount / total) * 100)) : (todayCheckInsForOfficer.length > 0 ? 100 : 0);
                        return (
                          <div className="mt-3">
                            <div className="flex items-center justify-between text-xs mb-1">
                              <span className="text-[hsl(var(--app-text-muted))]">Coverage today</span>
                              <span className="text-[hsl(var(--app-gold))] font-semibold">{coveragePct}%</span>
                            </div>
                            <div className="h-1.5 rounded-full bg-[hsl(var(--app-border-subtle))] overflow-hidden">
                              <div className="h-full rounded-full bg-[hsl(var(--app-gold))]" style={{ width: `${coveragePct}%` }} />
                            </div>
                          </div>
                        );
                      })()}
                      <div className="flex gap-2 mt-3 flex-wrap">
                        <button onClick={() => setFaceCaptureOfficer({ id: officer.id, name: officer.name, photoUrl: officer.face_photo_url })} className="py-2 px-3 rounded-lg text-xs font-medium border border-[hsl(var(--app-purple)/0.3)] text-[hsl(var(--app-purple))] hover:bg-[hsl(var(--app-purple)/0.1)] transition-colors flex items-center gap-1.5">
                          <ScanFace className="w-3.5 h-3.5" /> {officer.face_photo_url ? "Update Face" : "Capture Face"}
                        </button>
                        <button onClick={() => handleIssuePassword(officer.id)} className="flex-1 py-2 rounded-lg text-xs font-medium border border-[hsl(var(--app-gold)/0.3)] text-[hsl(var(--app-gold))] hover:bg-[hsl(var(--app-gold)/0.1)] transition-colors flex items-center justify-center gap-1.5">
                          <Key className="w-3.5 h-3.5" /> {hasValidPwd ? "Re-issue" : "Issue Password"}
                        </button>
                        {!officer.user_id && officer.username && (
                          <button 
                            onClick={() => handleFixOfficerAccount(officer.id, officer.username!, officer.name)} 
                            disabled={syncAccountLoading === officer.id}
                            className="flex-1 py-2 rounded-lg text-xs font-semibold bg-red-500/10 text-red-500 border border-red-500/20 hover:bg-red-500/20 transition-all flex items-center justify-center gap-1.5"
                          >
                            <AlertTriangle className="w-3.5 h-3.5" /> 
                            {syncAccountLoading === officer.id ? "Syncing..." : "Fix Account"}
                          </button>
                        )}
                        {officer.user_id && (
                          <button onClick={() => handleQuickTempLogin(officer.id, officer.name)} className="py-2 px-3 rounded-lg text-xs font-medium border border-[hsl(var(--app-red)/0.3)] text-[hsl(var(--app-red))] hover:bg-[hsl(var(--app-red)/0.1)] transition-colors flex items-center gap-1.5">
                            <Key className="w-3.5 h-3.5" /> Temp Login
                          </button>
                        )}
                        <button onClick={() => handleOpenEdit(officer)} className="py-2 px-3 rounded-lg text-xs font-medium border border-[hsl(var(--app-border-subtle))] text-[hsl(var(--app-text-muted))] hover:bg-[hsl(var(--app-surface-2))] transition-colors">Edit</button>
                      </div>
                    </div>
                    );
                  })}
                </div>
                {filteredOfficers.length === 0 && (
                  <div className="text-center py-12">
                    <Users className="w-10 h-10 text-[hsl(var(--app-text-muted))] mx-auto mb-3 opacity-40" />
                    <p className="text-sm text-[hsl(var(--app-text-muted))]">No officers yet</p>
                    <button onClick={() => setShowAddOfficerModal(true)} className="mt-3 text-xs text-[hsl(var(--app-gold))] hover:underline">Add your first officer</button>
                  </div>
                )}
              </div>
            )}

            {/* ========== SITES TAB ========== */}
            {activeTab === "sites" && (
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <h2 className="font-bebas text-2xl text-[hsl(var(--app-text-primary))]">MY SITES</h2>
                  <button onClick={() => setShowAddSite(true)} className="px-4 py-2 rounded-xl bg-[hsl(var(--app-gold))] text-[hsl(var(--app-bg))] text-sm font-semibold hover:brightness-110 transition-all flex items-center gap-2">
                    <Plus className="w-4 h-4" /> Add Site
                  </button>
                </div>

                {/* Add Site Modal */}
                <AnimatePresence>
                {showAddSite && (
                  <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="fixed inset-0 z-50 flex items-center justify-center bg-[hsl(var(--app-bg)/0.95)] backdrop-blur-sm">
                    <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} exit={{ opacity: 0, scale: 0.95 }} className="app-card rounded-2xl p-6 max-w-lg w-full mx-4 max-h-[90vh] overflow-y-auto">
                      <div className="flex items-center justify-between mb-5">
                        <h3 className="font-bebas text-xl text-[hsl(var(--app-text-primary))]">ADD NEW SITE</h3>
                        <button onClick={() => { setShowAddSite(false); if (siteMapInstanceRef.current) { siteMapInstanceRef.current.remove(); siteMapInstanceRef.current = null; } }} className="p-1.5 rounded-lg text-[hsl(var(--app-text-muted))] hover:text-[hsl(var(--app-text-secondary))] hover:bg-[hsl(var(--app-surface-2))] transition-colors">
                          <X className="w-4 h-4" />
                        </button>
                      </div>
                      <div className="space-y-4">
                        <div>
                          <label className="text-[10px] font-semibold text-[hsl(var(--app-text-muted))] uppercase tracking-wider mb-1 block">Site Name *</label>
                          <input value={newSite.name} onChange={e => setNewSite({ ...newSite, name: e.target.value })} placeholder="e.g. Riverside Office Park" className="w-full px-4 py-3 rounded-xl border border-[hsl(var(--app-border-subtle))] bg-[hsl(var(--app-surface-2))] text-sm text-[hsl(var(--app-text-primary))] placeholder:text-[hsl(var(--app-text-muted))] outline-none focus:border-[hsl(var(--app-gold))]" />
                        </div>
                        <div>
                          <label className="text-[10px] font-semibold text-[hsl(var(--app-text-muted))] uppercase tracking-wider mb-1 block">Address</label>
                          <input value={newSite.address} onChange={e => setNewSite({ ...newSite, address: e.target.value })} placeholder="e.g. 12 River Rd, Durban" className="w-full px-4 py-3 rounded-xl border border-[hsl(var(--app-border-subtle))] bg-[hsl(var(--app-surface-2))] text-sm text-[hsl(var(--app-text-primary))] placeholder:text-[hsl(var(--app-text-muted))] outline-none focus:border-[hsl(var(--app-gold))]" />
                        </div>
                        
                        {/* Map for site location */}
                        <div>
                          <label className="text-[10px] font-semibold text-[hsl(var(--app-text-muted))] uppercase tracking-wider mb-1 block">Site Location (drag pin to position)</label>
                          <button
                            onClick={() => {
                              if (navigator.geolocation) {
                                navigator.geolocation.getCurrentPosition(
                                  (pos) => {
                                    const lat = pos.coords.latitude;
                                    const lng = pos.coords.longitude;
                                    setNewSite(prev => ({ ...prev, lat, lng }));
                                    if (siteMarkerRef.current && siteMapInstanceRef.current) {
                                      siteMarkerRef.current.setLatLng([lat, lng]);
                                      siteMapInstanceRef.current.flyTo([lat, lng], 17);
                                    }
                                    toast.success("Location captured");
                                  },
                                  () => toast.error("Could not get GPS position")
                                );
                              }
                            }}
                            className="w-full py-2.5 rounded-xl border border-[hsl(var(--app-gold)/0.3)] text-sm font-medium text-[hsl(var(--app-gold))] hover:bg-[hsl(var(--app-gold)/0.1)] transition-colors flex items-center justify-center gap-2 mb-3"
                          >
                            <MapPin className="w-4 h-4" /> Use My Current Location
                          </button>
                          <div
                            ref={(node) => {
                              if (node && !siteMapInstanceRef.current) {
                                const loadMap = async () => {
                                  const L = await import("leaflet");
                                  if (!document.getElementById("leaflet-css")) {
                                    const link = document.createElement("link");
                                    link.id = "leaflet-css";
                                    link.rel = "stylesheet";
                                    link.href = "https://unpkg.com/leaflet@1.9.4/dist/leaflet.css";
                                    document.head.appendChild(link);
                                    await new Promise(r => setTimeout(r, 200));
                                  }
                                   const loc = userLocation || { lat: 0, lng: 0 };
                                  const map = L.map(node).setView([loc.lat, loc.lng], 15);
                                  siteMapInstanceRef.current = map;
                                  L.tileLayer("https://{s}.basemaps.cartocdn.com/dark_all/{z}/{x}/{y}{r}.png", { attribution: '&copy; CARTO' }).addTo(map);
                                  const goldIcon = L.divIcon({
                                    html: '<div style="width:24px;height:24px;border-radius:50%;background:#F0B429;border:3px solid white;box-shadow:0 0 12px rgba(240,180,41,0.5);cursor:grab;"></div>',
                                    iconSize: [24, 24], iconAnchor: [12, 12], className: '',
                                  });
                                  const marker = L.marker([loc.lat, loc.lng], { icon: goldIcon, draggable: true }).addTo(map);
                                  siteMarkerRef.current = marker;
                                  marker.on('dragend', () => {
                                    const pos = marker.getLatLng();
                                    setNewSite(prev => ({ ...prev, lat: pos.lat, lng: pos.lng }));
                                  });
                                  setNewSite(prev => ({ ...prev, lat: loc.lat, lng: loc.lng }));
                                  setTimeout(() => map.invalidateSize(), 200);
                                };
                                loadMap();
                              }
                            }}
                            className="w-full h-[250px] rounded-xl border border-[hsl(var(--app-gold)/0.3)] overflow-hidden bg-[hsl(var(--app-surface-2))]"
                          />
                          {newSite.lat != null && newSite.lng != null && (
                            <div className="flex items-center gap-2 p-2 rounded-lg bg-[hsl(var(--app-surface-2))] border border-[hsl(var(--app-border-subtle))] mt-2">
                              <MapPin className="w-3.5 h-3.5 text-[hsl(var(--app-gold))]" />
                              <span className="font-mono-data text-xs text-[hsl(var(--app-text-secondary))]">
                                {newSite.lat.toFixed(6)}, {newSite.lng.toFixed(6)}
                              </span>
                            </div>
                          )}
                        </div>

                        <div className="flex gap-3 mt-2">
                          <button onClick={() => { setShowAddSite(false); if (siteMapInstanceRef.current) { siteMapInstanceRef.current.remove(); siteMapInstanceRef.current = null; } }} className="flex-1 py-3 rounded-xl border border-[hsl(var(--app-border-subtle))] text-sm text-[hsl(var(--app-text-muted))] hover:bg-[hsl(var(--app-surface-2))] transition-colors">Cancel</button>
                          <button onClick={handleAddSite} className="flex-1 py-3 rounded-xl bg-[hsl(var(--app-gold))] text-[hsl(var(--app-bg))] text-sm font-semibold hover:brightness-110 transition-all flex items-center justify-center gap-1.5"><CheckCircle2 className="w-4 h-4" /> Add Site</button>
                        </div>
                      </div>
                    </motion.div>
                  </motion.div>
                )}
                </AnimatePresence>

                {sites.map((site) => (
                  <div key={site.id} className="app-card rounded-xl p-5">
                    <div className="flex items-start justify-between mb-3">
                      <div>
                        <h3 className="text-base font-semibold text-[hsl(var(--app-text-primary))]">{site.name}</h3>
                        <p className="text-xs text-[hsl(var(--app-text-muted))] mt-0.5 flex items-center gap-1"><MapPin className="w-3 h-3" /> {site.address}</p>
                      </div>
                      <button onClick={() => handleRemoveSite(site.id)} className="p-2 rounded-lg text-[hsl(var(--app-red)/0.6)] hover:bg-[hsl(var(--app-red)/0.1)] hover:text-[hsl(var(--app-red))] transition-colors">
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>

                    {/* Invite Code */}
                    {(site as any).invite_code && (
                      <div className="mb-4 p-3 rounded-xl bg-[hsl(var(--app-surface-2))] border border-[hsl(var(--app-gold)/0.2)]">
                        <div className="flex items-center justify-between">
                          <div>
                            <p className="text-[10px] font-semibold text-[hsl(var(--app-text-muted))] uppercase tracking-wider mb-1">Site Operator Invite Code</p>
                            <p className="font-mono-data text-lg font-bold text-[hsl(var(--app-gold))] tracking-[0.15em]">{(site as any).invite_code}</p>
                          </div>
                          <button
                            onClick={() => {
                              navigator.clipboard.writeText((site as any).invite_code);
                              toast.success("Invite code copied!");
                            }}
                            className="px-3 py-2 rounded-lg border border-[hsl(var(--app-gold)/0.3)] text-xs font-medium text-[hsl(var(--app-gold))] hover:bg-[hsl(var(--app-gold)/0.1)] transition-colors flex items-center gap-1.5"
                          >
                            <Copy className="w-3.5 h-3.5" /> Copy
                          </button>
                        </div>
                        <p className="text-[10px] text-[hsl(var(--app-text-muted))] mt-1.5">Share this code with the site operator so they can link this site to their dashboard</p>
                      </div>
                    )}
                    
                    <div className="grid grid-cols-2 gap-3 mb-4">
                      <div className="app-card-nested rounded-lg p-3 text-center">
                        <p className="font-bebas text-xl text-[hsl(var(--app-gold))]">{dbCheckpoints.filter(cp => cp.site_id === site.id).length}</p>
                        <p className="text-[10px] text-[hsl(var(--app-text-muted))] uppercase">Checkpoints</p>
                      </div>
                      <div className="app-card-nested rounded-lg p-3 text-center">
                        <p className="font-bebas text-xl text-[hsl(var(--app-blue))]">{getOfficersForSite(site.id).length}</p>
                        <p className="text-[10px] text-[hsl(var(--app-text-muted))] uppercase">Officers</p>
                      </div>
                    </div>

                    {/* Officers list */}
                    <div className="space-y-2">
                      <p className="text-xs font-semibold text-[hsl(var(--app-text-muted))] uppercase tracking-wider">Assigned Officers</p>
                      {getOfficersForSite(site.id).length > 0 ? (
                        getOfficersForSite(site.id).map((off) => (
                          <div key={off.id} className="flex items-center justify-between p-2.5 rounded-lg bg-[hsl(var(--app-surface-2))]">
                            <div className="flex items-center gap-2">
                              <span className={`w-2 h-2 rounded-full ${isOfficerLive(off) ? "bg-[hsl(var(--app-green))]" : "bg-[hsl(var(--app-border-active))]"}`} />
                              <span className="text-xs text-[hsl(var(--app-text-primary))]">{off.name}</span>
                            </div>
                          </div>
                        ))
                      ) : (
                        <p className="text-xs text-[hsl(var(--app-text-muted))] italic py-2">No officers assigned</p>
                      )}
                    </div>

                    {/* Add officer to site */}
                    {showAddOfficer === site.id ? (
                      <motion.div initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: "auto" }} className="mt-3 space-y-2">
                        <input value={newOfficer.name} onChange={e => setNewOfficer({ ...newOfficer, name: e.target.value })} placeholder="Officer full name" className="w-full px-3 py-2 rounded-lg border border-[hsl(var(--app-border-subtle))] bg-[hsl(var(--app-surface-2))] text-xs text-[hsl(var(--app-text-primary))] placeholder:text-[hsl(var(--app-text-muted))] outline-none focus:border-[hsl(var(--app-gold))]" />
                        <input value={newOfficer.username} onChange={e => setNewOfficer({ ...newOfficer, username: e.target.value.toLowerCase().replace(/\s/g, '') })} placeholder="Login username (e.g. j.doe)" className="w-full px-3 py-2 rounded-lg border border-[hsl(var(--app-border-subtle))] bg-[hsl(var(--app-surface-2))] text-xs text-[hsl(var(--app-text-primary))] placeholder:text-[hsl(var(--app-text-muted))] outline-none focus:border-[hsl(var(--app-gold))]" />
                        <div className="flex gap-2">
                          <button onClick={() => handleAddOfficerToSite(site.id)} className="flex-1 py-2 rounded-lg text-xs font-semibold bg-[hsl(var(--app-gold))] text-[hsl(var(--app-bg))]">Add</button>
                          <button onClick={() => setShowAddOfficer(null)} className="flex-1 py-2 rounded-lg text-xs border border-[hsl(var(--app-border-subtle))] text-[hsl(var(--app-text-muted))]">Cancel</button>
                        </div>
                      </motion.div>
                    ) : (
                      <button onClick={() => setShowAddOfficer(site.id)} className="mt-3 w-full py-2.5 rounded-lg border border-dashed border-[hsl(var(--app-border-active))] text-xs text-[hsl(var(--app-text-muted))] hover:border-[hsl(var(--app-gold))] hover:text-[hsl(var(--app-gold))] transition-colors flex items-center justify-center gap-1.5">
                        <Users className="w-3.5 h-3.5" /> Add Officer to Site
                      </button>
                    )}
                  </div>
                ))}
              </div>
            )}

            {/* ========== SETUP TAB ========== */}
            {activeTab === "setup" && (
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <div>
                    <h2 className="font-bebas text-2xl text-[hsl(var(--app-text-primary))]">CHECKPOINT CONFIGURATION</h2>
                    <p className="text-xs text-[hsl(var(--app-text-muted))]">Set the location and check-in method for each checkpoint. Drag the pin on the map to set position.</p>
                  </div>
                  <button onClick={() => setShowAddCheckpointModal(true)} className="px-4 py-2 rounded-xl bg-[hsl(var(--app-gold))] text-[hsl(var(--app-bg))] text-sm font-semibold hover:brightness-110 transition-all flex items-center gap-1.5">
                    <Plus className="w-4 h-4" /> Add Checkpoint
                  </button>
                </div>
                
                {/* Add Checkpoint Modal */}
                <AnimatePresence>
                  {showAddCheckpointModal && (
                    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="fixed inset-0 z-50 flex items-center justify-center bg-[hsl(var(--app-bg)/0.95)] backdrop-blur-sm">
                      <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} exit={{ opacity: 0, scale: 0.95 }} className="app-card rounded-2xl p-6 max-w-lg w-full mx-4 max-h-[90vh] overflow-y-auto">
                        <div className="flex items-center justify-between mb-5">
                          <h3 className="font-bebas text-xl text-[hsl(var(--app-text-primary))]">ADD NEW CHECKPOINT</h3>
                          <button onClick={() => { setShowAddCheckpointModal(false); if (addCpMapInstanceRef.current) { addCpMapInstanceRef.current.remove(); addCpMapInstanceRef.current = null; } }} className="p-1.5 rounded-lg text-[hsl(var(--app-text-muted))] hover:text-[hsl(var(--app-text-secondary))] hover:bg-[hsl(var(--app-surface-2))] transition-colors">
                            <X className="w-4 h-4" />
                          </button>
                        </div>
                        <div className="space-y-4">
                          <div className="grid grid-cols-2 gap-3">
                            <div>
                              <label className="text-[10px] font-semibold text-[hsl(var(--app-text-muted))] uppercase tracking-wider mb-1 block">Checkpoint Name *</label>
                              <input value={newCheckpoint.name} onChange={e => setNewCheckpoint(p => ({ ...p, name: e.target.value }))} placeholder="e.g. Main Gate" className="w-full px-4 py-3 rounded-xl border border-[hsl(var(--app-border-subtle))] bg-[hsl(var(--app-surface-2))] text-sm text-[hsl(var(--app-text-primary))] placeholder:text-[hsl(var(--app-text-muted))] outline-none focus:border-[hsl(var(--app-gold))]" />
                            </div>
                            <div>
                              <label className="text-[10px] font-semibold text-[hsl(var(--app-text-muted))] uppercase tracking-wider mb-1 block">Zone</label>
                              <input value={newCheckpoint.zone} onChange={e => setNewCheckpoint(p => ({ ...p, zone: e.target.value }))} placeholder="e.g. Perimeter A" className="w-full px-4 py-3 rounded-xl border border-[hsl(var(--app-border-subtle))] bg-[hsl(var(--app-surface-2))] text-sm text-[hsl(var(--app-text-primary))] placeholder:text-[hsl(var(--app-text-muted))] outline-none focus:border-[hsl(var(--app-gold))]" />
                            </div>
                          </div>
                          <div>
                            <label className="text-[10px] font-semibold text-[hsl(var(--app-text-muted))] uppercase tracking-wider mb-1 block">Assign to Site *</label>
                            <select value={newCheckpoint.siteId} onChange={e => setNewCheckpoint(p => ({ ...p, siteId: e.target.value }))} className="w-full px-4 py-3 rounded-xl border border-[hsl(var(--app-border-subtle))] bg-[hsl(var(--app-surface-2))] text-sm text-[hsl(var(--app-text-primary))] outline-none focus:border-[hsl(var(--app-gold))]">
                              <option value="">Select a site...</option>
                              {sites.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
                            </select>
                          </div>

                          {/* Method selector */}
                          <div>
                            <label className="text-[10px] font-semibold text-[hsl(var(--app-text-muted))] uppercase tracking-wider mb-2 block">Check-in Method</label>
                            <div className="grid grid-cols-2 gap-3">
                              <button
                                onClick={() => setNewCheckpoint(p => ({ ...p, method: "gps" }))}
                                className={`p-3 rounded-xl border text-left transition-all ${newCheckpoint.method === "gps" ? "border-[hsl(var(--app-gold))] bg-[hsl(var(--app-gold)/0.08)]" : "border-[hsl(var(--app-border-subtle))]"}`}
                              >
                                <MapPin className={`w-5 h-5 mb-1 ${newCheckpoint.method === "gps" ? "text-[hsl(var(--app-gold))]" : "text-[hsl(var(--app-text-muted))]"}`} />
                                <p className="text-xs font-semibold text-[hsl(var(--app-text-primary))]">GPS Geofence</p>
                                <p className="text-[10px] text-[hsl(var(--app-text-muted))]">Officer must be within radius</p>
                              </button>
                              <button
                                onClick={() => setNewCheckpoint(p => ({ ...p, method: "qr" }))}
                                className={`p-3 rounded-xl border text-left transition-all ${newCheckpoint.method === "qr" ? "border-[hsl(var(--app-gold))] bg-[hsl(var(--app-gold)/0.08)]" : "border-[hsl(var(--app-border-subtle))]"}`}
                              >
                                <ScanFace className={`w-5 h-5 mb-1 ${newCheckpoint.method === "qr" ? "text-[hsl(var(--app-gold))]" : "text-[hsl(var(--app-text-muted))]"}`} />
                                <p className="text-xs font-semibold text-[hsl(var(--app-text-primary))]">QR Code</p>
                                <p className="text-[10px] text-[hsl(var(--app-text-muted))]">Officer scans physical QR tag</p>
                              </button>
                            </div>
                          </div>

                          {newCheckpoint.method === "gps" && (
                            <div>
                              <div className="flex items-center justify-between mb-2">
                                <label className="text-[10px] font-semibold text-[hsl(var(--app-text-muted))] uppercase tracking-wider">Geofence Radius</label>
                                <span className="font-mono-data text-sm font-semibold text-[hsl(var(--app-gold))]">{newCheckpoint.radius}m</span>
                              </div>
                              <input type="range" min={10} max={100} step={5} value={newCheckpoint.radius}
                                onChange={(e) => {
                                  const r = Number(e.target.value);
                                  setNewCheckpoint(p => ({ ...p, radius: r }));
                                  if (addCpCircleRef.current) addCpCircleRef.current.setRadius(r);
                                }}
                                className="w-full h-2 rounded-full appearance-none cursor-pointer bg-[hsl(var(--app-border-subtle))] accent-[hsl(var(--app-gold))]"
                                style={{ background: `linear-gradient(to right, hsl(var(--app-gold)) 0%, hsl(var(--app-gold)) ${((newCheckpoint.radius - 10) / 90) * 100}%, hsl(var(--app-border-subtle)) ${((newCheckpoint.radius - 10) / 90) * 100}%, hsl(var(--app-border-subtle)) 100%)` }}
                              />
                            </div>
                          )}

                          {/* Map */}
                          <div>
                            <label className="text-[10px] font-semibold text-[hsl(var(--app-text-muted))] uppercase tracking-wider mb-1 block">Location (drag pin to position)</label>
                            <button
                              onClick={() => {
                                if (navigator.geolocation) {
                                  navigator.geolocation.getCurrentPosition(
                                    (pos) => {
                                      const lat = pos.coords.latitude;
                                      const lng = pos.coords.longitude;
                                      setNewCheckpoint(p => ({ ...p, lat, lng }));
                                      if (addCpMarkerRef.current && addCpMapInstanceRef.current) {
                                        addCpMarkerRef.current.setLatLng([lat, lng]);
                                        if (addCpCircleRef.current) addCpCircleRef.current.setLatLng([lat, lng]);
                                        addCpMapInstanceRef.current.flyTo([lat, lng], 17);
                                      }
                                      toast.success("Location captured");
                                    },
                                    () => toast.error("Could not get GPS position")
                                  );
                                }
                              }}
                              className="w-full py-2.5 rounded-xl border border-[hsl(var(--app-gold)/0.3)] text-sm font-medium text-[hsl(var(--app-gold))] hover:bg-[hsl(var(--app-gold)/0.1)] transition-colors flex items-center justify-center gap-2 mb-3"
                            >
                              <MapPin className="w-4 h-4" /> Use My Current Location
                            </button>
                            <div
                              ref={(node) => {
                                if (node && !addCpMapInstanceRef.current) {
                                  const loadMap = async () => {
                                    const L = await import("leaflet");
                                    if (!document.getElementById("leaflet-css")) {
                                      const link = document.createElement("link");
                                      link.id = "leaflet-css";
                                      link.rel = "stylesheet";
                                      link.href = "https://unpkg.com/leaflet@1.9.4/dist/leaflet.css";
                                      document.head.appendChild(link);
                                      await new Promise(r => setTimeout(r, 200));
                                    }
                                    const loc = userLocation || { lat: 0, lng: 0 };
                                    const map = L.map(node).setView([loc.lat, loc.lng], 17);
                                    addCpMapInstanceRef.current = map;
                                    L.tileLayer("https://{s}.basemaps.cartocdn.com/dark_all/{z}/{x}/{y}{r}.png", { attribution: '&copy; CARTO' }).addTo(map);
                                    const goldIcon = L.divIcon({
                                      html: '<div style="width:20px;height:20px;border-radius:50%;background:#F0B429;border:3px solid white;box-shadow:0 0 12px rgba(240,180,41,0.5);cursor:grab;"></div>',
                                      iconSize: [20, 20], iconAnchor: [10, 10], className: '',
                                    });
                                    const marker = L.marker([loc.lat, loc.lng], { icon: goldIcon, draggable: true }).addTo(map);
                                    addCpMarkerRef.current = marker;
                                    const circle = L.circle([loc.lat, loc.lng], {
                                      radius: newCheckpoint.radius, color: '#F0B429', fillColor: 'rgba(240,180,41,0.15)', fillOpacity: 0.3, weight: 2, dashArray: '6 4',
                                    }).addTo(map);
                                    addCpCircleRef.current = circle;
                                    marker.on('dragend', () => {
                                      const pos = marker.getLatLng();
                                      circle.setLatLng(pos);
                                      setNewCheckpoint(p => ({ ...p, lat: pos.lat, lng: pos.lng }));
                                    });
                                    setNewCheckpoint(p => ({ ...p, lat: loc.lat, lng: loc.lng }));
                                    setTimeout(() => map.invalidateSize(), 200);
                                  };
                                  loadMap();
                                }
                              }}
                              className="w-full h-[250px] rounded-xl border border-[hsl(var(--app-gold)/0.3)] overflow-hidden bg-[hsl(var(--app-surface-2))]"
                            />
                            {newCheckpoint.lat != null && newCheckpoint.lng != null && (
                              <div className="flex items-center gap-2 p-2 rounded-lg bg-[hsl(var(--app-surface-2))] border border-[hsl(var(--app-border-subtle))] mt-2">
                                <MapPin className="w-3.5 h-3.5 text-[hsl(var(--app-gold))]" />
                                <span className="font-mono-data text-xs text-[hsl(var(--app-text-secondary))]">
                                  {newCheckpoint.lat.toFixed(6)}, {newCheckpoint.lng.toFixed(6)}
                                </span>
                              </div>
                            )}
                          </div>

                          <div className="flex gap-3 mt-2">
                            <button onClick={() => { setShowAddCheckpointModal(false); if (addCpMapInstanceRef.current) { addCpMapInstanceRef.current.remove(); addCpMapInstanceRef.current = null; } }} className="flex-1 py-3 rounded-xl border border-[hsl(var(--app-border-subtle))] text-sm text-[hsl(var(--app-text-muted))] hover:bg-[hsl(var(--app-surface-2))] transition-colors">Cancel</button>
                            <button onClick={handleAddCheckpoint} className="flex-1 py-3 rounded-xl bg-[hsl(var(--app-gold))] text-[hsl(var(--app-bg))] text-sm font-semibold hover:brightness-110 transition-all flex items-center justify-center gap-1.5"><Plus className="w-4 h-4" /> Add Checkpoint</button>
                          </div>
                        </div>
                      </motion.div>
                    </motion.div>
                  )}
                </AnimatePresence>

                <div className="flex gap-4">
                  {/* Checkpoint list - now using DB checkpoints */}
                  <div className={`space-y-3 transition-all ${configCheckpoint ? "w-1/2 hidden md:block" : "w-full"}`}>
                    {(activeSite === "all" ? dbCheckpoints : dbCheckpoints.filter(cp => cp.site_id === activeSite)).map((cp) => {
                      const cfg = checkpointConfigs[cp.id];
                      const method = cfg?.method || cp.method;
                      const radius = cfg?.radius || cp.radius || 25;
                      const hasLoc = cfg?.lat != null || cp.lat != null;
                      return (
                        <div key={cp.id} className={`app-card rounded-xl p-5 transition-all ${configCheckpoint === cp.id ? "border-[hsl(var(--app-gold))] ring-1 ring-[hsl(var(--app-gold)/0.3)]" : ""}`}>
                          <div className="flex items-center justify-between flex-wrap gap-2">
                            <div>
                              <p className="text-sm font-semibold text-[hsl(var(--app-text-primary))]">{cp.name}</p>
                              <p className="text-xs text-[hsl(var(--app-text-muted))]">{cp.zone || getSiteName(cp.site_id)}</p>
                            </div>
                            <div className="flex items-center gap-3 flex-wrap">
                              <span className="px-2 py-1 rounded-full text-[10px] font-medium bg-[hsl(var(--app-surface-2))] text-[hsl(var(--app-text-secondary))]">
                                {method === "gps" ? `GPS ${radius}m` : "QR Code"}
                              </span>
                              <span className={`text-xs ${hasLoc ? "text-[hsl(var(--app-green))]" : "text-[hsl(var(--app-gold))]"}`}>
                                {hasLoc ? "Location set" : "No location"}
                              </span>
                              <button onClick={() => setConfigCheckpoint(configCheckpoint === cp.id ? null : cp.id)} className="px-3 py-1.5 rounded-lg text-xs font-medium border border-[hsl(var(--app-gold)/0.3)] text-[hsl(var(--app-gold))] hover:bg-[hsl(var(--app-gold)/0.1)] transition-colors">
                                {configCheckpoint === cp.id ? "Close" : "Configure →"}
                              </button>
                              <button onClick={async () => { await removeCheckpointMutation.mutateAsync(cp.id); toast.success("Checkpoint removed"); }} className="p-1.5 rounded-lg text-[hsl(var(--app-red)/0.4)] hover:bg-[hsl(var(--app-red)/0.1)] hover:text-[hsl(var(--app-red))] transition-colors">
                                <Trash2 className="w-3.5 h-3.5" />
                              </button>
                            </div>
                          </div>
                        </div>
                      );
                    })}

                    {dbCheckpoints.length === 0 && (
                      <div className="text-center py-12">
                        <MapPin className="w-10 h-10 text-[hsl(var(--app-text-muted))] mx-auto mb-3 opacity-40" />
                        <p className="text-sm text-[hsl(var(--app-text-muted))]">No checkpoints yet</p>
                        <button onClick={() => setShowAddCheckpointModal(true)} className="mt-3 text-xs text-[hsl(var(--app-gold))] hover:underline">Add your first checkpoint</button>
                      </div>
                    )}
                  </div>

                  {/* Configuration Panel */}
                  <AnimatePresence>
                    {configCheckpoint && (() => {
                      const cp = dbCheckpoints.find(c => c.id === configCheckpoint);
                      if (!cp) return null;
                      const cfg = checkpointConfigs[cp.id] || { method: cp.method, radius: cp.radius ?? 25, lat: cp.lat, lng: cp.lng };
                      const currentMethod = cfg.method;
                      const currentRadius = cfg.radius;
                      const currentLat = cfg.lat;
                      const currentLng = cfg.lng;

                      return (
                        <motion.div
                          key="config-panel"
                          initial={{ opacity: 0, x: 40 }}
                          animate={{ opacity: 1, x: 0 }}
                          exit={{ opacity: 0, x: 40 }}
                          className="w-full md:w-1/2 app-card rounded-xl p-5 space-y-5 self-start sticky top-32"
                        >
                          <div className="flex items-center justify-between">
                            <div>
                              <h3 className="font-bebas text-xl text-[hsl(var(--app-text-primary))]">{cp.name}</h3>
                              <p className="text-xs text-[hsl(var(--app-text-muted))]">{cp.zone}</p>
                            </div>
                            <button onClick={() => setConfigCheckpoint(null)} className="p-2 rounded-lg text-[hsl(var(--app-text-muted))] hover:text-[hsl(var(--app-text-secondary))] hover:bg-[hsl(var(--app-surface-2))] transition-colors">
                              <X className="w-4 h-4" />
                            </button>
                          </div>

                          {/* Method selector */}
                          <div>
                            <p className="text-xs font-semibold text-[hsl(var(--app-text-muted))] uppercase tracking-wider mb-3">Check-in Method</p>
                            <div className="grid grid-cols-2 gap-3">
                              <button
                                onClick={() => setCheckpointConfigs(prev => ({ ...prev, [cp.id]: { ...cfg, method: "gps" } }))}
                                className={`p-4 rounded-xl border-2 text-center transition-all ${
                                  currentMethod === "gps"
                                    ? "border-[hsl(var(--app-gold))] bg-[hsl(var(--app-gold)/0.08)]"
                                    : "border-[hsl(var(--app-border-subtle))] hover:border-[hsl(var(--app-border-active))]"
                                }`}
                              >
                                <MapPin className="w-6 h-6 text-[hsl(var(--app-gold))]" />
                                <p className="text-sm font-semibold text-[hsl(var(--app-text-primary))] mt-2">GPS Geofence</p>
                                <p className="text-[10px] text-[hsl(var(--app-text-muted))] mt-1">No hardware needed</p>
                              </button>
                              <button
                                onClick={() => setCheckpointConfigs(prev => ({ ...prev, [cp.id]: { ...cfg, method: "qr" } }))}
                                className={`p-4 rounded-xl border-2 text-center transition-all ${
                                  currentMethod === "qr"
                                    ? "border-[hsl(var(--app-gold))] bg-[hsl(var(--app-gold)/0.08)]"
                                    : "border-[hsl(var(--app-border-subtle))] hover:border-[hsl(var(--app-border-active))]"
                                }`}
                              >
                                <ScanFace className="w-6 h-6 text-[hsl(var(--app-purple))]" />
                                <p className="text-sm font-semibold text-[hsl(var(--app-text-primary))] mt-2">QR Code Scan</p>
                                <p className="text-[10px] text-[hsl(var(--app-text-muted))] mt-1">Scan at checkpoint</p>
                              </button>
                            </div>
                          </div>

                          {/* GPS Configuration */}
                          {currentMethod === "gps" && (
                            <div className="space-y-4">
                              {/* Radius slider */}
                              <div>
                                <div className="flex items-center justify-between mb-2">
                                  <p className="text-xs font-semibold text-[hsl(var(--app-text-muted))] uppercase tracking-wider">Geofence Radius</p>
                                  <span className="font-mono-data text-sm font-semibold text-[hsl(var(--app-gold))]">{currentRadius}m</span>
                                </div>
                                <input
                                  type="range"
                                  min={10}
                                  max={100}
                                  step={5}
                                  value={currentRadius}
                                  onChange={(e) => setCheckpointConfigs(prev => ({ ...prev, [cp.id]: { ...cfg, radius: Number(e.target.value) } }))}
                                  className="w-full h-2 rounded-full appearance-none cursor-pointer bg-[hsl(var(--app-border-subtle))] accent-[hsl(var(--app-gold))]"
                                  style={{
                                    background: `linear-gradient(to right, hsl(var(--app-gold)) 0%, hsl(var(--app-gold)) ${((currentRadius - 10) / 90) * 100}%, hsl(var(--app-border-subtle)) ${((currentRadius - 10) / 90) * 100}%, hsl(var(--app-border-subtle)) 100%)`
                                  }}
                                />
                                <div className="flex gap-2 mt-2">
                                  {[
                                    { label: "10m Indoor", value: 10 },
                                    { label: "25m Outdoor", value: 25 },
                                    { label: "50m Large Area", value: 50 },
                                  ].map((preset) => (
                                    <button
                                      key={preset.value}
                                      onClick={() => setCheckpointConfigs(prev => ({ ...prev, [cp.id]: { ...cfg, radius: preset.value } }))}
                                      className={`px-2.5 py-1 rounded-lg text-[10px] font-medium transition-all ${
                                        currentRadius === preset.value
                                          ? "bg-[hsl(var(--app-gold)/0.15)] text-[hsl(var(--app-gold))] border border-[hsl(var(--app-gold)/0.3)]"
                                          : "border border-[hsl(var(--app-border-subtle))] text-[hsl(var(--app-text-muted))] hover:border-[hsl(var(--app-border-active))]"
                                      }`}
                                    >
                                      {preset.label}
                                    </button>
                                  ))}
                                </div>
                              </div>

                              {/* Use My Location button */}
                              <button
                                onClick={() => {
                                  if (navigator.geolocation) {
                                    navigator.geolocation.getCurrentPosition(
                                      (pos) => {
                                        const lat = pos.coords.latitude;
                                        const lng = pos.coords.longitude;
                                        setCheckpointConfigs(prev => ({ ...prev, [cp.id]: { ...cfg, lat, lng } }));
                                        toast.success("Location captured successfully");
                                        // Update map marker
                                        if (configMapInstanceRef.current && configMarkerRef.current && configCircleRef.current) {
                                          const L = (window as any).L;
                                          configMarkerRef.current.setLatLng([lat, lng]);
                                          configCircleRef.current.setLatLng([lat, lng]);
                                          configMapInstanceRef.current.flyTo([lat, lng], 17);
                                        }
                                      },
                                      () => toast.error("Could not get GPS position")
                                    );
                                  }
                                }}
                                className="w-full py-3 rounded-xl bg-[hsl(var(--app-gold))] text-[hsl(var(--app-bg))] text-sm font-semibold hover:brightness-110 transition-all flex items-center justify-center gap-2"
                              >
                                <MapPin className="w-4 h-4" /> Use My Current Location
                              </button>

                              {/* Mini map */}
                              <ConfigMap
                                checkpointId={cp.id}
                                lat={currentLat}
                                lng={currentLng}
                                radius={currentRadius}
                                onPositionChange={(lat, lng) => setCheckpointConfigs(prev => ({ ...prev, [cp.id]: { ...cfg, lat, lng } }))}
                                configMapRef={configMapRef}
                                configMapInstanceRef={configMapInstanceRef}
                                configMarkerRef={configMarkerRef}
                                configCircleRef={configCircleRef}
                                userLocation={userLocation}
                              />

                              {/* Coordinates display */}
                              {currentLat != null && currentLng != null && (
                                <div className="flex items-center gap-2 p-3 rounded-lg bg-[hsl(var(--app-surface-2))] border border-[hsl(var(--app-border-subtle))]">
                                  <MapPin className="w-3.5 h-3.5 text-[hsl(var(--app-gold))]" />
                                  <span className="font-mono-data text-xs text-[hsl(var(--app-text-secondary))]">
                                    {currentLat.toFixed(6)}, {currentLng.toFixed(6)}
                                  </span>
                                </div>
                              )}
                            </div>
                          )}

                          {/* QR Code Configuration */}
                          {currentMethod === "qr" && (
                            <div className="space-y-4">
                              <div className="flex flex-col items-center p-6 rounded-xl bg-[hsl(var(--app-surface-2))] border border-[hsl(var(--app-border-subtle))]">
                                {/* Generated QR Code SVG */}
                                <div className="w-[180px] h-[180px] bg-white rounded-xl p-3 flex items-center justify-center">
                                  <svg viewBox="0 0 100 100" className="w-full h-full">
                                    {/* QR code pattern generated from checkpoint ID */}
                                    {generateQRPattern(cp.id, cp.name).map((rect, i) => (
                                      <rect key={i} x={rect.x} y={rect.y} width={rect.w} height={rect.h} fill="#03050D" />
                                    ))}
                                  </svg>
                                </div>
                                <p className="font-mono-data text-[10px] text-[hsl(var(--app-text-muted))] mt-3">
                                  SF-CP-{cp.id.toUpperCase().padStart(4, '0')}-{cp.name.replace(/\s/g, '').substring(0, 4).toUpperCase()}
                                </p>
                              </div>

                              <button
                                onClick={() => {
                                  const printWindow = window.open('', '_blank');
                                  if (printWindow) {
                                    printWindow.document.write(`
                                      <html>
                                        <head><title>SecureFlex QR Code</title>
                                        <style>
                                          body { font-family: Arial, sans-serif; display: flex; flex-direction: column; align-items: center; justify-content: center; min-height: 100vh; margin: 0; }
                                          .logo { font-size: 24px; font-weight: bold; letter-spacing: 4px; margin-bottom: 8px; }
                                          .sub { color: #666; font-size: 12px; margin-bottom: 32px; }
                                          .cp-name { font-size: 18px; font-weight: bold; margin-bottom: 4px; }
                                          .cp-zone { color: #666; font-size: 14px; margin-bottom: 24px; }
                                          .qr { width: 200px; height: 200px; border: 2px solid #000; padding: 12px; border-radius: 12px; }
                                          .code { font-family: monospace; font-size: 11px; color: #666; margin-top: 16px; }
                                          .instruction { font-size: 12px; color: #999; margin-top: 24px; }
                                        </style></head>
                                        <body>
                                          <div class="logo">SECUREFLEX</div>
                                          <div class="sub">Patrol Management System</div>
                                          <div class="cp-name">${cp.name}</div>
                                          <div class="cp-zone">${cp.zone}</div>
                                          <div class="qr">
                                            <svg viewBox="0 0 100 100" style="width:100%;height:100%">
                                              ${generateQRPattern(cp.id, cp.name).map(rect => `<rect x="${rect.x}" y="${rect.y}" width="${rect.w}" height="${rect.h}" fill="#000"/>`).join('')}
                                            </svg>
                                          </div>
                                          <div class="code">SF-CP-${cp.id.toUpperCase().padStart(4, '0')}-${cp.name.replace(/\s/g, '').substring(0, 4).toUpperCase()}</div>
                                          <div class="instruction">Scan with SecureFlex app to check in</div>
                                        </body>
                                      </html>
                                    `);
                                    printWindow.document.close();
                                    printWindow.print();
                                  }
                                }}
                                className="w-full py-3 rounded-xl border border-[hsl(var(--app-border-active))] text-sm text-[hsl(var(--app-text-secondary))] hover:border-[hsl(var(--app-gold))] hover:text-[hsl(var(--app-gold))] transition-colors flex items-center justify-center gap-2"
                              >
                                <Printer className="w-4 h-4" /> Download & Print QR Code
                              </button>
                            </div>
                          )}

                          {/* Save button */}
                          <button
                            onClick={() => {
                              toast.success(`${cp.name} configuration saved`);
                              setConfigCheckpoint(null);
                            }}
                            className="w-full py-3 rounded-xl bg-[hsl(var(--app-green))] text-white text-sm font-semibold hover:brightness-110 transition-all"
                          >
                            <CheckCircle2 className="w-4 h-4 inline mr-1" /> Save Configuration
                          </button>
                        </motion.div>
                      );
                    })()}
                  </AnimatePresence>
                </div>
              </div>
            )}

            {/* ========== CLIENTS TAB ========== */}
            {activeTab === "clients" && (
              <div className="space-y-6">
                <div className="flex items-center justify-between flex-wrap gap-3">
                  <h2 className="font-bebas text-2xl text-[hsl(var(--app-text-primary))]">MY CLIENTS</h2>
                  <div className="flex items-center gap-3">
                    <span className="text-sm text-[hsl(var(--app-text-muted))]">{clientsList.length} client{clientsList.length !== 1 ? "s" : ""}</span>
                    <button
                      onClick={() => setShowAddClient(true)}
                      className="flex items-center gap-2 px-4 py-2 rounded-xl bg-[hsl(var(--app-gold))] text-[hsl(var(--app-bg))] text-sm font-semibold hover:brightness-110 transition-all"
                    >
                      <Plus className="w-4 h-4" /> Add Client
                    </button>
                  </div>
                </div>

                <p className="text-xs text-[hsl(var(--app-text-muted))]">
                  Clients are site operators linked to your sites. They appear automatically when they claim a site using your invite code, or you can add them manually.
                </p>

                {/* Search */}
                <div className="flex items-center gap-3 px-4 py-2.5 rounded-xl border border-[hsl(var(--app-border-subtle))] bg-[hsl(var(--app-surface-2))] focus-within:border-[hsl(var(--app-gold))] transition-colors">
                  <Eye className="w-4 h-4 text-[hsl(var(--app-text-muted))]" />
                  <input
                    type="text"
                    placeholder="Search clients by name, email, or phone..."
                    value={clientSearch}
                    onChange={(e) => setClientSearch(e.target.value)}
                    className="flex-1 bg-transparent text-sm text-[hsl(var(--app-text-primary))] placeholder:text-[hsl(var(--app-text-muted))] outline-none font-outfit"
                  />
                  {clientSearch && (
                    <button onClick={() => setClientSearch("")} className="text-[hsl(var(--app-text-muted))] hover:text-[hsl(var(--app-text-primary))]">
                      <X className="w-4 h-4" />
                    </button>
                  )}
                </div>

                {/* Add Client Modal */}
                {showAddClient && (
                  <motion.div initial={{ opacity: 0, y: -10 }} animate={{ opacity: 1, y: 0 }} className="app-card rounded-xl p-6 space-y-4">
                    <div className="flex items-center justify-between">
                      <h3 className="font-bebas text-lg text-[hsl(var(--app-text-primary))]">ADD NEW CLIENT</h3>
                      <button onClick={() => setShowAddClient(false)} className="text-[hsl(var(--app-text-muted))] hover:text-[hsl(var(--app-text-primary))]"><X className="w-4 h-4" /></button>
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                      <input placeholder="Client Name *" value={newClient.name} onChange={(e) => setNewClient(f => ({ ...f, name: e.target.value }))} className="w-full px-4 py-3 rounded-xl border border-[hsl(var(--app-border-subtle))] bg-[hsl(var(--app-surface-2))] text-sm text-[hsl(var(--app-text-primary))] placeholder:text-[hsl(var(--app-text-muted))] outline-none focus:border-[hsl(var(--app-gold))] transition-colors font-outfit" />
                      <input placeholder="Email" value={newClient.email} onChange={(e) => setNewClient(f => ({ ...f, email: e.target.value }))} className="w-full px-4 py-3 rounded-xl border border-[hsl(var(--app-border-subtle))] bg-[hsl(var(--app-surface-2))] text-sm text-[hsl(var(--app-text-primary))] placeholder:text-[hsl(var(--app-text-muted))] outline-none focus:border-[hsl(var(--app-gold))] transition-colors font-outfit" />
                      <input placeholder="Phone" value={newClient.phone} onChange={(e) => setNewClient(f => ({ ...f, phone: e.target.value }))} className="w-full px-4 py-3 rounded-xl border border-[hsl(var(--app-border-subtle))] bg-[hsl(var(--app-surface-2))] text-sm text-[hsl(var(--app-text-primary))] placeholder:text-[hsl(var(--app-text-muted))] outline-none focus:border-[hsl(var(--app-gold))] transition-colors font-outfit" />
                      <select value={newClient.siteId} onChange={(e) => setNewClient(f => ({ ...f, siteId: e.target.value }))} className="w-full px-4 py-3 rounded-xl border border-[hsl(var(--app-border-subtle))] bg-[hsl(var(--app-surface-2))] text-sm text-[hsl(var(--app-text-muted))] outline-none focus:border-[hsl(var(--app-gold))] transition-colors font-outfit">
                        <option value="">Link to Site (optional)</option>
                        {sites.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
                      </select>
                    </div>
                    <textarea placeholder="Notes (optional)" value={newClient.notes} onChange={(e) => setNewClient(f => ({ ...f, notes: e.target.value }))} rows={2} className="w-full px-4 py-3 rounded-xl border border-[hsl(var(--app-border-subtle))] bg-[hsl(var(--app-surface-2))] text-sm text-[hsl(var(--app-text-primary))] placeholder:text-[hsl(var(--app-text-muted))] outline-none focus:border-[hsl(var(--app-gold))] transition-colors font-outfit resize-none" />
                    <button onClick={handleAddClient} disabled={addingClient} className="px-6 py-3 rounded-xl bg-[hsl(var(--app-gold))] text-[hsl(var(--app-bg))] font-semibold text-sm hover:brightness-110 transition-all disabled:opacity-50">
                      {addingClient ? "Adding..." : "Add Client"}
                    </button>
                  </motion.div>
                )}

                {clientsLoading ? (
                  <div className="text-center py-12 text-[hsl(var(--app-text-muted))]">Loading clients...</div>
                ) : (() => {
                  const q = clientSearch.toLowerCase();
                  const filtered = clientsList.filter(c => {
                    const matchSearch = !q || c.client_name.toLowerCase().includes(q) || (c.client_email || "").toLowerCase().includes(q) || (c.client_phone || "").toLowerCase().includes(q);
                    return matchSearch;
                  });
                  return filtered.length === 0 ? (
                    <div className="app-card rounded-xl p-8 text-center">
                      <UserCircle className="w-12 h-12 mx-auto mb-3 text-[hsl(var(--app-text-muted))]" />
                      <p className="text-sm text-[hsl(var(--app-text-muted))]">{clientsList.length === 0 ? "No clients yet. Share your site invite codes or add clients manually." : "No clients match your search"}</p>
                    </div>
                  ) : (
                    <div className="app-card rounded-xl overflow-hidden">
                      <div className="overflow-x-auto">
                        <table className="w-full text-sm">
                          <thead>
                            <tr className="border-b border-[hsl(var(--app-border-subtle))]">
                              <th className="px-4 py-3 text-left text-xs font-semibold text-[hsl(var(--app-text-muted))] uppercase tracking-wider">Name</th>
                              <th className="px-4 py-3 text-left text-xs font-semibold text-[hsl(var(--app-text-muted))] uppercase tracking-wider">Email</th>
                              <th className="px-4 py-3 text-left text-xs font-semibold text-[hsl(var(--app-text-muted))] uppercase tracking-wider">Phone</th>
                              <th className="px-4 py-3 text-left text-xs font-semibold text-[hsl(var(--app-text-muted))] uppercase tracking-wider">Site</th>
                              <th className="px-4 py-3 text-left text-xs font-semibold text-[hsl(var(--app-text-muted))] uppercase tracking-wider">Source</th>
                              <th className="px-4 py-3 text-left text-xs font-semibold text-[hsl(var(--app-text-muted))] uppercase tracking-wider">Added</th>
                              <th className="px-4 py-3 text-left text-xs font-semibold text-[hsl(var(--app-text-muted))] uppercase tracking-wider"></th>
                            </tr>
                          </thead>
                          <tbody>
                            {filtered.map((client) => {
                              const linkedSite = sites.find(s => s.id === client.site_id);
                              return (
                                <tr key={client.id} className="border-b border-[hsl(var(--app-border-subtle))] last:border-0 hover:bg-[hsl(var(--app-surface-2))] transition-colors">
                                  <td className="px-4 py-3 text-[hsl(var(--app-text-primary))] font-medium">{client.client_name}</td>
                                  <td className="px-4 py-3 text-[hsl(var(--app-text-secondary))]">{client.client_email || "—"}</td>
                                  <td className="px-4 py-3 text-[hsl(var(--app-text-secondary))]">{client.client_phone || "—"}</td>
                                  <td className="px-4 py-3 text-[hsl(var(--app-text-secondary))]">{linkedSite?.name || "—"}</td>
                                  <td className="px-4 py-3">
                                    <span className={`px-2 py-0.5 rounded-full text-[10px] font-bold uppercase ${
                                      client.added_via === "invite_code"
                                        ? "bg-[hsl(var(--app-blue)/0.15)] text-[hsl(var(--app-blue))]"
                                        : "bg-[hsl(var(--app-gold)/0.15)] text-[hsl(var(--app-gold))]"
                                    }`}>
                                      {client.added_via === "invite_code" ? "Invite Code" : "Manual"}
                                    </span>
                                  </td>
                                  <td className="px-4 py-3 text-[hsl(var(--app-text-muted))] text-xs">{new Date(client.created_at).toLocaleDateString()}</td>
                                  <td className="px-4 py-3">
                                    <button onClick={() => handleRemoveClient(client.id)} className="text-[hsl(var(--app-text-muted))] hover:text-red-400 transition-colors">
                                      <Trash2 className="w-4 h-4" />
                                    </button>
                                  </td>
                                </tr>
                              );
                            })}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  );
                })()}
              </div>
            )}

            {/* ========== BILLING TAB ========== */}
            {activeTab === "billing" && (
              <div className="space-y-6">
                <h2 className="font-bebas text-2xl text-[hsl(var(--app-text-primary))]">BILLING & PLAN</h2>

                {/* Promo Code */}
                <PromoCodeRedeem currentPlan={subscription?.plan} />
                
                {/* Current Plan / Trial Status */}
                <div className="app-card rounded-xl p-6">
                  <div className="flex items-center justify-between mb-3">
                    <div>
                      <p className="font-bebas text-2xl text-[hsl(var(--app-gold))]">
                        {subscription?.status === 'active' ? subscription.plan.toUpperCase() + ' PLAN' : 'FREE TRIAL'}
                      </p>
                      <p className="text-xs text-[hsl(var(--app-text-muted))]">
                        {subscription?.status === 'active' 
                          ? `Active since ${new Date(subscription.activated_at || '').toLocaleDateString()}`
                          : 'Full access for 30 days'}
                      </p>
                    </div>
                    {subscription?.status !== 'active' && (() => {
                      const totalDays = 30;
                      const pct = Math.round(((totalDays - daysLeft) / totalDays) * 100);
                      return (
                        <div className="text-right">
                          <span className={`font-bebas text-xl ${daysLeft <= 7 ? 'text-[hsl(var(--app-red))]' : 'text-[hsl(var(--app-gold))]'}`}>
                            {daysLeft} days remaining
                          </span>
                          <div className="h-2 w-32 mt-1 rounded-full bg-[hsl(var(--app-border-subtle))] overflow-hidden">
                            <div className={`h-full rounded-full ${daysLeft <= 7 ? 'bg-[hsl(var(--app-red))]' : 'bg-[hsl(var(--app-gold))]'}`} style={{ width: `${100 - pct}%` }} />
                          </div>
                        </div>
                      );
                    })()}
                    {subscription?.status === 'active' && (
                      <span className="px-4 py-2 rounded-full text-sm font-bold bg-[hsl(var(--app-green)/0.15)] text-[hsl(var(--app-green))] border border-[hsl(var(--app-green)/0.3)]">
                        ✓ Active
                      </span>
                    )}
                  </div>
                </div>

                {/* Upgrade Confirmation Modal */}
                {showUpgradeConfirm && selectedPlan && (
                  <motion.div initial={{ opacity: 0, y: -10 }} animate={{ opacity: 1, y: 0 }} className="app-card rounded-xl p-6 border-2 border-[hsl(var(--app-gold))]">
                    <h3 className="font-bebas text-xl text-[hsl(var(--app-text-primary))] mb-2">CONFIRM UPGRADE TO {getPlan(selectedPlan).name}</h3>
                    <p className="text-sm text-[hsl(var(--app-text-secondary))] mb-1">
                      ✓ All your existing data, sites, officers and settings will be preserved
                    </p>
                    <p className="text-sm text-[hsl(var(--app-text-secondary))] mb-1">
                      ✓ Your dashboard progress carries over seamlessly
                    </p>
                    <p className="text-sm text-[hsl(var(--app-text-secondary))] mb-4">
                      ✓ Upgrade takes effect immediately — no downtime
                    </p>
                    <div className="flex gap-3">
                      <button
                        onClick={() => {
                          setShowUpgradeConfirm(false);
                          if (selectedPlan) openLemonCheckout(selectedPlan);
                        }}
                        className="px-6 py-3 rounded-xl bg-[hsl(var(--app-gold))] text-[hsl(var(--app-bg))] font-bold hover:brightness-110 transition-all flex items-center gap-2"
                      >
                        <CreditCard className="w-4 h-4" />
                        Pay {getPlanPrice(selectedPlan)}/mo — Secure Checkout
                      </button>
                      <button
                        onClick={() => setShowUpgradeConfirm(false)}
                        className="px-6 py-3 rounded-xl border border-[hsl(var(--app-border-active))] text-[hsl(var(--app-text-secondary))] hover:text-[hsl(var(--app-text-primary))] transition-all"
                      >
                        Cancel
                      </button>
                    </div>
                  </motion.div>
                )}

                {/* Plans */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  {PLANS.map((plan) => {
                    const isCurrentPlan = subscription?.status === 'active' && subscription.plan === plan.key;
                    const isSelected = selectedPlan === plan.key;
                    return (
                      <div key={plan.name} className={`app-card rounded-xl p-6 relative transition-all cursor-pointer ${
                        isCurrentPlan
                          ? "border-[hsl(var(--app-green))] ring-2 ring-[hsl(var(--app-green)/0.3)]"
                          : isSelected
                            ? "border-[hsl(var(--app-gold))] gold-glow ring-2 ring-[hsl(var(--app-gold)/0.3)]"
                            : plan.popular && !selectedPlan
                              ? "border-[hsl(var(--app-gold))] gold-glow"
                              : "hover:border-[hsl(var(--app-border-active))]"
                      }`} onClick={() => { if (!isCurrentPlan) setSelectedPlan(plan.key); }}>
                        {isCurrentPlan && <span className="absolute -top-3 left-6 px-3 py-1 rounded-full text-[10px] font-bold bg-[hsl(var(--app-green))] text-[hsl(var(--app-bg))]">Current Plan</span>}
                        {!isCurrentPlan && plan.popular && !selectedPlan && <span className="absolute -top-3 left-6 px-3 py-1 rounded-full text-[10px] font-bold bg-[hsl(var(--app-gold))] text-[hsl(var(--app-bg))]">Most Popular</span>}
                        {!isCurrentPlan && isSelected && <span className="absolute -top-3 left-6 px-3 py-1 rounded-full text-[10px] font-bold bg-[hsl(var(--app-gold))] text-[hsl(var(--app-bg))]">Selected</span>}
                        <p className="font-bebas text-xl text-[hsl(var(--app-text-primary))]">{plan.name}</p>
                        <p className="text-xs text-[hsl(var(--app-text-muted))]">{plan.desc}</p>
                        <p className="font-bebas text-3xl text-[hsl(var(--app-text-primary))] mt-3">{plan.price}<span className="text-sm font-outfit font-normal text-[hsl(var(--app-text-muted))]">/month</span></p>
                        
                        {/* Collapsible feature list */}
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            setExpandedBillingPlan(prev => prev === plan.key ? null : plan.key);
                          }}
                          className="flex items-center gap-1.5 mt-4 text-xs text-[hsl(var(--app-gold))] hover:brightness-125 transition-all"
                        >
                          <span>{expandedBillingPlan === plan.key ? "Hide features" : "View all features"}</span>
                          <ChevronDown className={`w-3.5 h-3.5 transition-transform duration-300 ${expandedBillingPlan === plan.key ? "rotate-180" : ""}`} />
                        </button>
                        <div className={`overflow-hidden transition-all duration-300 ${expandedBillingPlan === plan.key ? "max-h-[500px] opacity-100 mt-3" : "max-h-0 opacity-0"}`}>
                          <ul className="space-y-2 border-t border-[hsl(var(--app-border-subtle))] pt-3">
                            {plan.features.map((f) => (
                              <li key={f} className="flex items-center gap-2 text-xs text-[hsl(var(--app-text-secondary))]">
                                <CheckCircle2 className="w-3 h-3 text-[hsl(var(--app-gold))] flex-shrink-0" /> {f}
                              </li>
                            ))}
                          </ul>
                        </div>

                        <button
                          onClick={(e) => { 
                            e.stopPropagation(); 
                            if (isCurrentPlan) return;
                            openLemonCheckout(plan.key);
                          }}
                          className={`mt-6 w-full py-3 rounded-xl text-sm font-semibold transition-all flex items-center justify-center gap-2 ${
                            isCurrentPlan
                              ? "bg-[hsl(var(--app-green)/0.15)] text-[hsl(var(--app-green))] cursor-default"
                              : isSelected
                                ? "bg-[hsl(var(--app-gold))] text-[hsl(var(--app-bg))] hover:brightness-110"
                                : "border border-[hsl(var(--app-border-active))] text-[hsl(var(--app-text-secondary))] hover:border-[hsl(var(--app-gold))] hover:text-[hsl(var(--app-gold))]"
                          }`}>
                          {isCurrentPlan ? (
                            <>✓ Current Plan</>
                          ) : (
                            <><CreditCard className="w-3.5 h-3.5" /> Upgrade Now</>
                          )}
                        </button>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {/* ========== TIMESHEETS TAB ========== */}
            {activeTab === "timesheets" && (
              <TimesheetSection
                officers={officersList.map(o => ({ id: o.id, name: o.name, site_id: o.site_id }))}
                sites={sites.map(s => ({ id: s.id, name: s.name }))}
              />
            )}

            {/* ========== PROFILE TAB ========== */}
            {activeTab === "profile" && <ProfileSection />}

            {/* ========== SUPPORT TAB ========== */}
            {activeTab === "support" && <SupportTicketForm />}

          </motion.div>
        </AnimatePresence>
      </main>

      {/* Bottom nav (mobile) */}
      <nav className="fixed bottom-0 left-0 right-0 md:hidden border-t border-[hsl(var(--app-border-subtle))] app-surface-1 z-40 backdrop-blur-sm">
        <div className="grid grid-cols-7 gap-0">
          {tabs.slice(0, 7).map((tab) => {
            const Icon = tab.icon;
            return (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`py-2 flex flex-col items-center gap-0.5 text-[8px] font-semibold transition-colors ${
                  activeTab === tab.id ? "text-[hsl(var(--app-gold))]" : "text-[hsl(var(--app-text-muted))]"
                }`}
              >
                <Icon className="w-4 h-4" />
                {tab.label}
              </button>
            );
          })}
        </div>
        <div className="grid grid-cols-7 gap-0 border-t border-[hsl(var(--app-border-subtle)/0.5)]">
          {tabs.slice(7).map((tab) => {
            const Icon = tab.icon;
            return (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`py-2 flex flex-col items-center gap-0.5 text-[8px] font-semibold transition-colors ${
                  activeTab === tab.id ? "text-[hsl(var(--app-gold))]" : "text-[hsl(var(--app-text-muted))]"
                }`}
              >
                <Icon className="w-4 h-4" />
                {tab.label}
              </button>
            );
          })}
        </div>
      </nav>

      {/* Face Capture Modal */}
      <AnimatePresence>
        {faceCaptureOfficer && (
          <FaceCaptureModal
            officerId={faceCaptureOfficer.id}
            officerName={faceCaptureOfficer.name}
            currentPhotoUrl={faceCaptureOfficer.photoUrl}
            onClose={() => setFaceCaptureOfficer(null)}
            onCaptured={() => setFaceCaptureOfficer(null)}
          />
        )}
      </AnimatePresence>
    </div>
  );
};

export default BusinessOwnerDashboard;
